import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ESTIMATING CREATE PAGE
 *
 * Page for creating new estimation/quote with work breakdown
 * Uses the same work details structure as the Edit page
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Checkbox, CommencementDateInput, DateInput, PageHeader, PageHeaderWithBack, useToast, } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useDeposit } from "@/context/DepositContext";
import { quotesService, } from "@/api/services/quotes.service";
import { sitesService } from "@/api/services/sites.service";
import { contactsService } from "@/api/services/contacts.service";
import { applyMainOtherFromCostParams, } from "./mainJobOtherUtils";
import { formatFullName, getContactDisplayName } from "@/utils/string";
import { calculateTotalCost, getShopfrontSettings, getShopfrontSettingsFromItems, } from "@/utils/costCalculations";
import { searchSuburbsAsync } from "@/data/australian-suburbs";
import { isWeekend } from "@/utils/dateTime";
import { isUuid } from "@/utils/validation";
import { resolveShoppingCentreIdFromContact } from "@/utils/shoppingCentreMatch";
import { SearchableShoppingCentreSelect } from "@/features/admin/contacts/components/SearchableShoppingCentreSelect";
import styles from "./EstimatingEdit.module.css";
// Work category tabs - same as Edit page
const WORK_DETAILS_CATEGORY_TABS = [
    { id: "compliance", label: "Compliance/Startup" },
    { id: "service_trades", label: "Service Trades" },
    { id: "flooring", label: "Flooring" },
    { id: "internal_demolition", label: "Internal Demolition" },
    { id: "preliminary_cost", label: "Preliminary Cost" },
    { id: "summary", label: "Summary" },
];
// Default area information template
const DEFAULT_AREA_INFO = [
    { label: "Tenancy Defit (m2)", value: 0 },
    { label: "Wall Finish (Plaster) (m2)", value: 0 },
    { label: "Wall Finish (Brickwork) (m2)", value: 0 },
    { label: "Shopfront (l/m)", value: 0 },
    { label: "Shopfront (under 3.6m) OR of Kiosk Lm (m2)", value: 0 },
];
// Helper function to merge areaInfo with default template
const mergeAreaInfo = (sourceAreaInfo, defaultTemplate) => {
    return defaultTemplate.map((defaultItem) => {
        const sourceItem = sourceAreaInfo?.find((item) => item.label === defaultItem.label) ?? null;
        return {
            ...defaultItem,
            value: sourceItem?.value ?? defaultItem.value,
        };
    });
};
// Default accordion structure for each category - same as Edit page
const getDefaultAccordionData = () => ({
    compliance: [
        {
            id: "demolition",
            title: "Demolition",
            sections: [
                {
                    sectionId: "hoardings",
                    sectionTitle: "Hoardings (note rates are on m2 basis)",
                    items: [
                        {
                            id: "hoarding-internal-solid",
                            title: "Internal Solid Hoarding",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "hoarding-kiosk",
                            title: "Kiosk Hoarding",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "compliance-main",
            title: "Compliance",
            sections: [
                {
                    sectionId: "permits",
                    items: [
                        {
                            id: "comp-demolition-permit",
                            title: "Demolition Permit",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "comp-building-permit",
                            title: "Building Permit",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
    ],
    service_trades: [
        {
            id: "services-electrical",
            title: "Services - Electrical",
            sections: [
                {
                    sectionId: "electrical",
                    items: [
                        {
                            id: "elec-1",
                            title: "Isolate and Make Safe Tenancy Single EDB and IDF",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "elec-2",
                            title: "Isolate and Make Safe Tenancy Dual EDB and IDF",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "fire",
            title: "Fire",
            sections: [
                {
                    sectionId: "fire",
                    showQty: true,
                    items: [
                        {
                            id: "fire-1",
                            title: "Sprinkler – plugging up to 3m",
                            inScope: false,
                            qty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fire-2",
                            title: "Wet System Drain Down and Recommission - note each means 1 drain down and recommission",
                            inScope: false,
                            qty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fire-3",
                            title: "Smoke Detector – relocation up to 3m",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                            hideQty: true,
                        },
                        {
                            id: "fire-4",
                            title: "Electronic smoke alarm isolation/de-isolation",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                            hideQty: true,
                        },
                        {
                            id: "fire-5",
                            title: "EWIS – relocation up to 10m with Fig. 8 cable",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                            hideQty: true,
                        },
                    ],
                },
            ],
        },
        {
            id: "hydraulics",
            title: "Hydraulics",
            sections: [
                {
                    sectionId: "hydraulics",
                    showQty: true,
                    items: [
                        {
                            id: "hyd-1",
                            title: "Attendance including Cap and Seal Hydraulics Points - 1 water and 1 waste water",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                            hideQty: true,
                        },
                        {
                            id: "hyd-2",
                            title: "Cap and Seal additional hydraulics points - 1 water and 1 waste water",
                            inScope: false,
                            qty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "hyd-3",
                            title: "Penetration of plaster ceiling and backfill",
                            inScope: false,
                            qty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                            hideQty: true,
                        },
                    ],
                },
            ],
        },
        {
            id: "hvac",
            title: "HVAC - Tenant Supplementary Units",
            sections: [
                {
                    sectionId: "hvac",
                    items: [
                        {
                            id: "hvac-1",
                            title: "Split ducted - In ceiling space air cooled FCU with external condenser",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "hvac-2",
                            title: "Split cassette - Ceiling mounted integrated air cooled unit with external condenser",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "hvac-3",
                            title: "Wall hung split - Wall mounted air cooled unit with external condenser",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "hvac-4",
                            title: "Split ducted - water cooled no or internally located condenser",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "it-wall",
            title: "IT Wall",
            sections: [
                {
                    sectionId: "it-wall",
                    items: [
                        {
                            id: "it-wall-1",
                            title: "Supply and install plasterboard to perimeter IT walls",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "it-wall-2",
                            title: "Supply and install new IT wall (13mm Plasterboard either side with 92mm Stud)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "it-wall-3",
                            title: "Relocate EDB (Switchboard)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "it-wall-4",
                            title: "Supply Paint",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "it-wall-5",
                            title: "Relocate Hydraulic Services (underground trenching)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "it-wall-6",
                            title: "Relocate Hydraulic Services (above ground)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
    ],
    flooring: [
        {
            id: "floor-finishes",
            title: "Floor Finishes",
            sections: [
                {
                    sectionId: "floor-finishes",
                    items: [
                        {
                            id: "ff-1",
                            title: "Remove Ceramic Tiles (Direct Stick)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ff-2",
                            title: "Remove Vinyl (Walk behind)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ff-3",
                            title: "Remove Carpet Tiles",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ff-4",
                            title: "Remove Timber / Parquetry (Direct Stick)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ff-5",
                            title: "After Hours Works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "screeds",
            title: "Screeds",
            sections: [
                {
                    sectionId: "screeds",
                    items: [
                        {
                            id: "screeds-1",
                            title: "0mm - 50mm",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "screeds-2",
                            title: "50mm - 80mm",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "screeds-3",
                            title: "After Hours works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "grinding",
            title: "Grinding",
            sections: [
                {
                    sectionId: "grinding",
                    items: [
                        {
                            id: "grinding-1",
                            title: "Tenancy Floor Grinding",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "grinding-2",
                            title: "After Hours works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
    ],
    internal_demolition: [
        {
            id: "kiosks",
            title: "Kiosks",
            sections: [
                {
                    sectionId: "kiosks",
                    items: [
                        {
                            id: "kiosks-1",
                            title: "Specialty",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "kiosks-2",
                            title: "Food & Beverage",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "kiosks-3",
                            title: "Make Good of Mall Tiles",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "joinery-fixtures",
            title: "Joinery & Fixtures",
            sections: [
                {
                    sectionId: "joinery",
                    sectionTitle: "Tenancy Area (0-500m2)",
                    items: [
                        {
                            id: "joinery-0-500-low",
                            title: "Low volume of FF&E & Joinery Disposal (General Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-0-500-moderate",
                            title: "Moderate volume of FF&E & Joinery Disposal (Premium Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-0-500-high-food",
                            title: "High volume of FF&E & Joinery Disposal (Food Service incl. Cool Room)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-0-500-high-jewellers",
                            title: "High volume of FF&E & Joinery Disposal (Jewellers)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
                {
                    sectionId: "joinery",
                    sectionTitle: "Tenancy Area (500-1000m2)",
                    items: [
                        {
                            id: "joinery-500-1000-low",
                            title: "Low volume of FF&E & Joinery Disposal (General Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-500-1000-moderate",
                            title: "Moderate volume of FF&E & Joinery Disposal (Premium Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-500-1000-high-food",
                            title: "High volume of FF&E & Joinery Disposal (Food Service incl. Cool Room)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-500-1000-high-jewellers",
                            title: "High volume of FF&E & Joinery Disposal (Jewellers)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
                {
                    sectionId: "joinery",
                    sectionTitle: "Tenancy Area (1000m2+)",
                    items: [
                        {
                            id: "joinery-1000-plus-low",
                            title: "Low volume of FF&E & Joinery Disposal (General Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-1000-plus-moderate",
                            title: "Moderate volume of FF&E & Joinery Disposal (Premium Specialty Retail)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-1000-plus-high-food",
                            title: "High volume of FF&E & Joinery Disposal (Food Service incl. Cool Room)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "joinery-1000-plus-high-jewellers",
                            title: "High volume of FF&E & Joinery Disposal (Jewellers)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "demolish-walls",
            title: "Demolish Walls",
            sections: [
                {
                    sectionId: "walls",
                    items: [
                        {
                            id: "walls-1",
                            title: "Remove Blockwork Walls (Single Skin)",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "walls-2",
                            title: "Remove Stud IT Walls",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "walls-3",
                            title: "After Hours works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "demolish-shopfronts",
            title: "Demolish Shopfronts",
            sections: [
                {
                    sectionId: "shopfronts",
                    items: [
                        {
                            id: "shopfront-1",
                            title: "Standard glazed & MDF",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "shopfront-2",
                            title: "Heavy Duty Steel & Stone",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "shopfront-3",
                            title: "Shopfront Height Factor Std",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "shopfront-4",
                            title: "Shopfront Height Factor Hvy",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "shopfront-3a",
                            title: "Shopfront is OVER 3.6m in height",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "shopfront-5",
                            title: "After Hours Works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "ceiling-type",
            title: "Ceiling Type",
            sections: [
                {
                    sectionId: "ceiling",
                    sectionTitle: "Ceiling Type - to include demolition of mechanical diffusers",
                    items: [
                        {
                            id: "ceiling-1",
                            title: "Open Plan Ceiling",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ceiling-2",
                            title: "Remove Ceiling Tiles & Grid",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ceiling-3",
                            title: "Remove Plaster Lining & Gridwork",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "ceiling-4",
                            title: "After Hours Works Required",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
    ],
    preliminary_cost: [
        {
            id: "prelims-supervision",
            title: "Prelims & Supervision",
            sections: [
                {
                    sectionId: "prelims",
                    items: [
                        {
                            id: "prelims-1",
                            title: "Prelims & Supervision",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "scissor-lift",
            title: "Scissor Lift Hire",
            sections: [
                {
                    sectionId: "scissor-lift",
                    showWeeksQty: true,
                    items: [
                        {
                            id: "scissor-1",
                            title: '"19" Electric',
                            inScope: false,
                            weeksQty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "skip-hire",
            title: "Skip Hire",
            sections: [
                {
                    sectionId: "skip-hire",
                    showNumberQty: true,
                    items: [
                        {
                            id: "skip-1",
                            title: "12m3",
                            inScope: false,
                            numberQty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "skip-2",
                            title: "15m3",
                            inScope: false,
                            numberQty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "skip-3",
                            title: "23m3",
                            inScope: false,
                            numberQty: "",
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
        {
            id: "other-fees",
            title: "Other Fees Price on Application",
            sections: [
                {
                    sectionId: "other-fees",
                    items: [
                        {
                            id: "fees-1",
                            title: "Specialist consultant - Electrical",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fees-2",
                            title: "Specialist consultant - Hydraulic",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fees-3",
                            title: "Specialist consultant - Structural",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fees-4",
                            title: "Protection works",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                        {
                            id: "fees-5",
                            title: "Specialist Cleaning",
                            inScope: false,
                            unitCost: "",
                            totalCost: "",
                            notes: "",
                        },
                    ],
                },
            ],
        },
    ],
    summary: [],
});
// Helper to parse currency string to number
const parseCurrency = (value) => {
    if (!value)
        return 0;
    const cleaned = value.replace(/[^0-9.-]/g, "");
    return parseFloat(cleaned) || 0;
};
// Helper to format number as currency
const formatCurrency = (value) => {
    return `$${value.toLocaleString("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};
// Helper to calculate sum of Standard glazed & MDF and Heavy Duty Steel & Stone totals
const getShopfrontItemsTotal = (items, shopfrontSettings) => {
    let total = 0;
    items.forEach((item) => {
        const isStandardGlazed = item.id === "shopfront-1";
        const isHeavyDuty = item.id === "shopfront-2";
        if ((isStandardGlazed || isHeavyDuty) && item.inScope && item.totalCost) {
            let itemTotal = parseCurrency(item.totalCost);
            // Apply height factor if toggle is ON
            if (shopfrontSettings?.heightEnabled) {
                if (isStandardGlazed) {
                    itemTotal *= shopfrontSettings.stdHeightFactor;
                }
                else if (isHeavyDuty) {
                    itemTotal *= shopfrontSettings.hvyHeightFactor;
                }
            }
            total += itemTotal;
        }
    });
    return total;
};
// Helper to format unit cost for display (decimal without $)
export const EstimatingCreate = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { user, isAdmin } = useAuth();
    const { error: showError } = useToast();
    const canEditPrices = isAdmin; // Only admins can edit prices
    // URL params
    const siteIdParam = searchParams.get("siteId");
    const isNewSite = searchParams.get("newSite") === "true";
    // Form state
    const [contactId, setContactId] = useState("");
    const [siteId, setSiteId] = useState("");
    const [clientName, setClientName] = useState("");
    const [defitType, setDefitType] = useState("full");
    const [quoteName, setQuoteName] = useState("");
    const [quoteDescription, setQuoteDescription] = useState("");
    const [commencementDate, setCommencementDate] = useState("");
    const [commencementTime, setCommencementTime] = useState("");
    const [completionDate, setCompletionDate] = useState("");
    const [completionTime, setCompletionTime] = useState("");
    const [projectDuration, setProjectDuration] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [loadingSite, setLoadingSite] = useState(false);
    const [loadedSiteData, setLoadedSiteData] = useState(null);
    const [shoppingCentreId, setShoppingCentreId] = useState("");
    // New site creation fields
    const [newSiteName, setNewSiteName] = useState("");
    const [newTenancyNumber, setNewTenancyNumber] = useState("");
    const [newTenancyName, setNewTenancyName] = useState("");
    const [newBuildingType, setNewBuildingType] = useState("");
    const [newLandlord, setNewLandlord] = useState("");
    const [newAddress, setNewAddress] = useState("");
    const [newSuburb, setNewSuburb] = useState("");
    const [newState, setNewState] = useState("");
    const [newPostcode, setNewPostcode] = useState("");
    const [newSiteCompanyName, setNewSiteCompanyName] = useState("");
    // Dropdowns
    const [sites, setSites] = useState([]);
    // Searchable company dropdown state (like EstimatingEdit)
    const companyDropdownRef = useRef(null);
    const [companyNames, setCompanyNames] = useState([]);
    const [companyContactIdMap, setCompanyContactIdMap] = useState(new Map());
    const [loadingCompanyNames, setLoadingCompanyNames] = useState(false);
    const [companySearchTerm, setCompanySearchTerm] = useState("");
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [selectedCompanyName, setSelectedCompanyName] = useState("");
    // Company contacts for attention field
    const [companyContacts, setCompanyContacts] = useState([]);
    // Searchable attention dropdown state
    const attentionDropdownRef = useRef(null);
    const [attentionContactId, setAttentionContactId] = useState("");
    const [attentionSearchTerm, setAttentionSearchTerm] = useState("");
    const [showAttentionDropdown, setShowAttentionDropdown] = useState(false);
    const [selectedAttentionContactName, setSelectedAttentionContactName] = useState("");
    const [attentionAutoFilled, setAttentionAutoFilled] = useState(false);
    // Suburb dropdown state
    const suburbDropdownRef = useRef(null);
    const [suburbSearchTerm, setSuburbSearchTerm] = useState("");
    const [showSuburbDropdown, setShowSuburbDropdown] = useState(false);
    const [filteredSuburbs, setFilteredSuburbs] = useState([]);
    // Area information
    const [areaInfo, setAreaInfo] = useState(DEFAULT_AREA_INFO);
    // Cost parameters state
    const [costParameters, setCostParameters] = useState([]);
    const [costParametersLoaded, setCostParametersLoaded] = useState(false);
    // Work details state
    const [activeWorkCategory, setActiveWorkCategory] = useState("compliance");
    const [expandedAccordions, setExpandedAccordions] = useState(new Set());
    const [accordionData, setAccordionData] = useState(getDefaultAccordionData);
    const { defaultDepositPercentage } = useDeposit();
    // Financial state
    const [buildersMarginEnabled, setBuildersMarginEnabled] = useState(true);
    const [buildersMarginPercentage, setBuildersMarginPercentage] = useState(10);
    const [includeNewClientTerms, setIncludeNewClientTerms] = useState(false);
    const [depositEnabled, setDepositEnabled] = useState(false);
    const [depositPercentage, setDepositPercentage] = useState(defaultDepositPercentage);
    useEffect(() => {
        if (includeNewClientTerms && !depositEnabled) {
            setDepositEnabled(true);
        }
    }, [includeNewClientTerms, depositEnabled]);
    // Fetch sites for dropdown
    const fetchSites = useCallback(async () => {
        try {
            const response = await sitesService.getSites({ pageSize: 100 });
            const siteOptions = response.data.data.map((site) => ({
                id: site.id,
                name: site.siteName,
            }));
            setSites(siteOptions);
        }
        catch (err) {
            console.error("Error fetching sites:", err);
        }
    }, []);
    // Fetch cost parameters
    const fetchCostParameters = useCallback(async () => {
        try {
            const response = await sitesService.getDefaultWorkItems();
            setCostParameters(response.data);
            setCostParametersLoaded(true);
        }
        catch (err) {
            console.error("Error fetching cost parameters:", err);
            setCostParametersLoaded(true); // Still mark as loaded even on error
        }
    }, []);
    // Fetch company names and contactId mapping for searchable dropdown
    const fetchCompanyNames = useCallback(async () => {
        try {
            setLoadingCompanyNames(true);
            // Fetch companies with their contact IDs so we can look up contactId when selecting
            const response = await contactsService.getCompaniesWithContactIds();
            const companiesData = response.data.data || [];
            // Build the company name -> contactId map
            const newMap = new Map();
            companiesData.forEach((company) => {
                if (company.companyName && company.contactId) {
                    newMap.set(company.companyName, company.contactId);
                }
            });
            setCompanyContactIdMap(newMap);
            // Extract unique company names and filter based on search term
            const allCompanyNames = Array.from(newMap.keys());
            let filteredNames = allCompanyNames;
            if (companySearchTerm && companySearchTerm.trim()) {
                const searchLower = companySearchTerm.toLowerCase().trim();
                filteredNames = allCompanyNames.filter((name) => name.toLowerCase().includes(searchLower));
            }
            setCompanyNames(filteredNames);
        }
        catch (err) {
            console.error("Error fetching company names:", err);
            setCompanyNames([]);
        }
        finally {
            setLoadingCompanyNames(false);
        }
    }, [companySearchTerm]);
    // Handle company selection from searchable dropdown
    const handleCompanySelect = useCallback(async (companyName) => {
        setSelectedCompanyName(companyName);
        setCompanySearchTerm(companyName);
        setShowCompanyDropdown(false);
        setClientName(companyName);
        setNewSiteCompanyName(companyName);
        // Clear attention contact only when company actually changes
        // When prefilled from Sites, user may re-select same company - preserve attention
        if (companyName !== selectedCompanyName) {
            setAttentionContactId("");
            setSelectedAttentionContactName("");
            setAttentionSearchTerm("");
        }
        // Get the contactId from the map (still needed for setting initial contactId)
        const companyContactId = companyContactIdMap.get(companyName);
        if (companyContactId) {
            setContactId(companyContactId);
        }
        // Fetch company contacts for the attention dropdown using companyName filter
        // This API call filters contacts by company name - only contacts belonging to this company are returned
        // The contacts are then stored in companyContacts state and filtered by search term in filteredAttentionContacts
        try {
            const contactResponse = await contactsService.getContacts({
                companyName: companyName,
                page: 1,
            });
            // Map Contact[] to CompanyContact[] format
            const companyContactsData = (contactResponse.data.data || []).map((contact) => ({
                contactId: contact.contactId,
                firstName: contact.firstName,
                lastName: contact.lastName,
                email: contact.email,
                phone: contact.phone,
                role: contact.role,
            }));
            setCompanyContacts(companyContactsData);
        }
        catch (err) {
            console.error("Error fetching company contacts:", err);
            setCompanyContacts([]);
        }
    }, [companyContactIdMap, selectedCompanyName]);
    // Handle clearing company selection
    // When company is cleared, also clear all related contacts to ensure Attention dropdown
    // only shows contacts for the selected company
    const handleClearCompany = useCallback(() => {
        setSelectedCompanyName("");
        setCompanySearchTerm("");
        setContactId("");
        setClientName("");
        setNewSiteCompanyName("");
        setAttentionContactId("");
        setSelectedAttentionContactName("");
        setAttentionSearchTerm("");
        setCompanyContacts([]); // Clear contacts when company is cleared
    }, []);
    // Handle attention contact selection
    const handleAttentionContactSelect = useCallback((contact) => {
        const displayName = getContactDisplayName(contact.firstName, contact.lastName, contact.email);
        // Set attentionContactId to the selected attention contact's ID
        setAttentionContactId(contact.contactId);
        // Keep clientName as company name (Client = company, Attention = contact)
        setSelectedAttentionContactName(displayName);
        setAttentionSearchTerm(displayName);
        setShowAttentionDropdown(false);
    }, []);
    // Handle clearing attention contact selection
    const handleClearAttentionContact = useCallback(() => {
        setAttentionContactId("");
        // Reset clientName back to company name
        setClientName(selectedCompanyName);
        setSelectedAttentionContactName("");
        setAttentionSearchTerm("");
    }, [selectedCompanyName]);
    // Filtered contacts for attention dropdown based on search term
    // Contacts are already filtered by company via API call to getContacts({ companyName })
    // This ensures only contacts belonging to the selected company are shown
    const filteredAttentionContacts = useMemo(() => {
        // Only show contacts if a company is selected
        if (!selectedCompanyName) {
            return [];
        }
        // Contacts are already filtered by company via API call to getContacts({ companyName })
        // Filter by search term (name/email)
        if (!attentionSearchTerm || !attentionSearchTerm.trim()) {
            return companyContacts;
        }
        const searchLower = attentionSearchTerm.toLowerCase().trim();
        return companyContacts.filter((contact) => {
            const fullName = formatFullName(contact.firstName, contact.lastName).toLowerCase();
            const email = (contact.email || "").toLowerCase();
            return fullName.includes(searchLower) || email.includes(searchLower);
        });
    }, [companyContacts, attentionSearchTerm, selectedCompanyName]);
    // Handle suburb selection
    const handleSuburbSelect = useCallback((suburb) => {
        setNewSuburb(suburb.name);
        setNewState(suburb.state);
        setNewPostcode(suburb.postcode);
        setSuburbSearchTerm(suburb.name);
        setShowSuburbDropdown(false);
    }, []);
    // Handle clearing suburb selection
    const handleClearSuburb = useCallback(() => {
        setSuburbSearchTerm("");
        setNewSuburb("");
        setFilteredSuburbs([]);
    }, []);
    // Filter suburbs based on search term (fallback to Matthew Proctor if not in local list)
    useEffect(() => {
        if (suburbSearchTerm.trim()) {
            searchSuburbsAsync(suburbSearchTerm).then((results) => {
                setFilteredSuburbs(results.slice(0, 50));
            });
        }
        else {
            setFilteredSuburbs([]);
        }
    }, [suburbSearchTerm]);
    // Fetch company names when dropdown is shown
    useEffect(() => {
        if (showCompanyDropdown) {
            fetchCompanyNames();
        }
    }, [showCompanyDropdown, fetchCompanyNames]);
    // Handle click outside to close company dropdown
    useEffect(() => {
        if (!showCompanyDropdown)
            return;
        const container = companyDropdownRef.current;
        if (!container)
            return;
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (container && target && !container.contains(target)) {
                setShowCompanyDropdown(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setShowCompanyDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [showCompanyDropdown]);
    // Handle click outside to close attention dropdown
    useEffect(() => {
        if (!showAttentionDropdown)
            return;
        const container = attentionDropdownRef.current;
        if (!container)
            return;
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (container && target && !container.contains(target)) {
                setShowAttentionDropdown(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setShowAttentionDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [showAttentionDropdown]);
    // Handle click outside to close suburb dropdown
    useEffect(() => {
        if (!showSuburbDropdown)
            return;
        const container = suburbDropdownRef.current;
        if (!container)
            return;
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (container && target && !container.contains(target)) {
                setShowSuburbDropdown(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setShowSuburbDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [showSuburbDropdown]);
    // Map cost parameters to accordion data structure
    const mapCostParametersToAccordion = useCallback((defaultData, costParams) => {
        const mappedData = { ...defaultData };
        costParams.forEach((cp) => {
            const category = cp.category;
            if (!mappedData[category])
                return;
            // Find the matching accordion item
            const accordionItem = mappedData[category].find((item) => item.sections?.some((section) => section.sectionId === cp.sectionId));
            if (accordionItem) {
                // Search through all sections to find the item by itemId
                // (multiple sections can have the same sectionId, e.g., joinery)
                for (const section of accordionItem.sections || []) {
                    const subItem = section.items.find((item) => item.id === cp.itemId);
                    if (subItem) {
                        // Store defaultUnitCost from cost settings for later use (e.g., when toggling After Hours items)
                        subItem.defaultUnitCost =
                            cp.defaultUnitCost ?? cp.unitCost ?? null;
                        if (cp.unitCost) {
                            // Update unit cost as plain number string (preserves decimals, matching SiteDetailsTab behavior)
                            subItem.unitCost = `${cp.unitCost}`;
                        }
                        break; // Found the item, no need to continue searching
                    }
                }
            }
        });
        // Surface the client-editable Main Job "Other" rows (group2–group5),
        // which arrive with prefixed sectionIds the per-item loop above can't match.
        applyMainOtherFromCostParams(mappedData, costParams);
        return mappedData;
    }, []);
    // Merge work items from site into accordion structure
    const mergeWorkItems = useCallback((workItems) => {
        if (!workItems || workItems.length === 0)
            return;
        setAccordionData((prevData) => {
            const newData = JSON.parse(JSON.stringify(prevData)); // Deep clone
            workItems.forEach((item) => {
                const category = item.category?.toLowerCase();
                if (!category || !newData[category])
                    return;
                const accordionItem = newData[category].find((acc) => acc.sections?.some((s) => s.sectionId === item.sectionId));
                if (accordionItem?.sections) {
                    const section = accordionItem.sections.find((s) => {
                        if (s.sectionId === item.sectionId) {
                            // For joinery sections, also match by sectionTitle
                            if (item.sectionId === "joinery" && item.sectionTitle) {
                                return s.sectionTitle === item.sectionTitle;
                            }
                            return true;
                        }
                        return false;
                    });
                    if (section) {
                        const subItem = section.items.find((i) => i.id === item.itemId);
                        if (subItem) {
                            subItem.inScope = item.inScope;
                            // Store defaultUnitCost for later use (e.g., when toggling After Hours items)
                            subItem.defaultUnitCost = item.defaultUnitCost ?? null;
                            // Use saved unitCost if set, otherwise use defaultUnitCost from cost parameters
                            const unitCostValue = item.unitCost != null
                                ? item.unitCost
                                : (item.defaultUnitCost ?? null);
                            subItem.unitCost =
                                unitCostValue != null ? `${unitCostValue}` : "";
                            // Only set totalCost if inScope is true
                            subItem.totalCost =
                                item.inScope && item.totalCost != null
                                    ? formatCurrency(item.totalCost)
                                    : "";
                            subItem.notes = item.notes || "";
                            // Map quantity to the appropriate field based on sectionId
                            if (item.quantity !== null && item.quantity !== undefined) {
                                // Map quantity to the appropriate field based on sectionId
                                if (section.sectionId === "scissor-lift") {
                                    subItem.weeksQty = item.quantity.toString();
                                }
                                else if (section.sectionId === "skip-hire") {
                                    subItem.numberQty = item.quantity.toString();
                                }
                                else {
                                    subItem.qty = item.quantity.toString();
                                }
                            }
                            if (item.selectedOption) {
                                subItem.selectedOption = item.selectedOption;
                            }
                        }
                    }
                }
            });
            return newData;
        });
    }, []);
    const resolveShoppingCentrePrefillForSite = useCallback(async (site, companyContactId) => {
        let derived = site.shoppingCentreId ??
            site.shoppingCentre?.id ??
            (isUuid(site.shoppingCenter ?? "") ? site.shoppingCenter : "") ??
            "";
        if (derived)
            return derived;
        if (companyContactId) {
            const resolved = await resolveShoppingCentreIdFromContact(companyContactId);
            if (resolved)
                return resolved;
        }
        return "";
    }, []);
    // Fetch site data when siteId param exists
    const fetchSiteData = useCallback(async (siteId) => {
        try {
            setLoadingSite(true);
            // Reset attention auto-fill flag when loading a new site
            setAttentionAutoFilled(false);
            const response = await sitesService.getSite(siteId);
            const site = response.data;
            // Pre-populate form fields from site data
            setSiteId(site.id);
            setNewSiteName(site.siteName || "");
            setNewTenancyNumber(site.tenancyNumber || "");
            setNewTenancyName(site.tenancyName || "");
            setNewBuildingType(site.buildingTypes?.[0] || "");
            setNewLandlord(site.landlord || "");
            setNewAddress(site.address || "");
            const suburbValue = site.suburb || "";
            setNewSuburb(suburbValue);
            setSuburbSearchTerm(suburbValue);
            setNewState(site.state || "");
            setNewPostcode(site.postcode || "");
            // Merge site's areaInfo with default template to ensure all items are present
            setAreaInfo(mergeAreaInfo(site.areaInfo, DEFAULT_AREA_INFO));
            // Set company name (Client Name) from site
            let companyContactIdForFallback;
            if (site.companyName) {
                setClientName(site.companyName);
                setSelectedCompanyName(site.companyName);
                setCompanySearchTerm(site.companyName);
                setNewSiteCompanyName(site.companyName);
                // Fetch company names first to populate companyContactIdMap
                try {
                    const companiesResponse = await contactsService.getCompaniesWithContactIds();
                    const companiesData = companiesResponse.data.data || [];
                    const newMap = new Map();
                    companiesData.forEach((company) => {
                        if (company.companyName && company.contactId) {
                            newMap.set(company.companyName, company.contactId);
                        }
                    });
                    setCompanyContactIdMap(newMap);
                    // Get company contactId for setting contactId
                    const companyContactId = newMap.get(site.companyName);
                    companyContactIdForFallback = companyContactId;
                    if (companyContactId) {
                        setContactId(companyContactId);
                    }
                }
                catch (err) {
                    console.error("Error fetching company names:", err);
                }
                // Fetch company contacts for attention dropdown
                try {
                    const contactResponse = await contactsService.getContacts({
                        companyName: site.companyName,
                        page: 1,
                    });
                    // Map Contact[] to CompanyContact[] format
                    const companyContactsData = (contactResponse.data.data || []).map((contact) => ({
                        contactId: contact.contactId,
                        firstName: contact.firstName,
                        lastName: contact.lastName,
                        email: contact.email,
                        phone: contact.phone,
                        role: contact.role,
                    }));
                    setCompanyContacts(companyContactsData);
                }
                catch (err) {
                    console.error("Error fetching company contacts:", err);
                    setCompanyContacts([]);
                }
            }
            // Store site data for later work items merging (after cost parameters are loaded)
            // Attention field will be auto-filled via useEffect after company contacts are loaded
            setLoadedSiteData(site);
            const shoppingCentrePrefill = await resolveShoppingCentrePrefillForSite(site, companyContactIdForFallback);
            setShoppingCentreId(shoppingCentrePrefill);
        }
        catch (err) {
            console.error("Error fetching site:", err);
            setError("Failed to load site data");
        }
        finally {
            setLoadingSite(false);
        }
    }, [resolveShoppingCentrePrefillForSite]);
    useEffect(() => {
        if (!isNewSite) {
            fetchSites();
        }
        fetchCostParameters();
        // Fetch site data if siteId param exists
        if (siteIdParam) {
            fetchSiteData(siteIdParam);
        }
    }, [fetchSites, fetchCostParameters, siteIdParam, isNewSite, fetchSiteData]);
    // Auto-fill attention field from site's contact after company contacts are loaded
    useEffect(() => {
        // Only proceed if we have site data with contact
        if (!loadedSiteData?.contactPerson) {
            return;
        }
        const siteContact = loadedSiteData.contactPerson;
        // Skip if attention was manually changed by user (not from this site's contact)
        // If attentionAutoFilled is true and attentionContactId doesn't match contactPerson.contactId,
        // it means user manually selected a different contact, so don't overwrite
        if (attentionAutoFilled &&
            attentionContactId &&
            attentionContactId !== siteContact.contactId) {
            return;
        }
        // Try to find matching contact in companyContacts by ID, or by email if ID mismatch
        // (site API may return contactPerson with different contactId format than contacts API)
        const matchedContact = companyContacts.length > 0
            ? (companyContacts.find((contact) => contact.contactId === siteContact.contactId) ??
                (siteContact.email
                    ? companyContacts.find((c) => c.email?.toLowerCase() === siteContact.email?.toLowerCase())
                    : null))
            : null;
        if (matchedContact) {
            // Found match - use CompanyContact format
            const displayName = getContactDisplayName(matchedContact.firstName, matchedContact.lastName, matchedContact.email);
            setAttentionContactId(matchedContact.contactId);
            setSelectedAttentionContactName(displayName);
            setAttentionSearchTerm(displayName);
            // Keep clientName as company name (Client = company, Attention = contact)
            setAttentionAutoFilled(true);
        }
        else if (!attentionAutoFilled ||
            attentionContactId === siteContact.contactId) {
            // No match found or companyContacts not loaded yet - use site contact data as fallback
            // Also allow upgrade if we're currently using siteContact.contactId
            // Prefer full name; backend may return "name" when firstName/lastName are empty
            const contactDisplayName = getContactDisplayName(siteContact.firstName, siteContact.lastName, siteContact.email) ||
                siteContact.name ||
                siteContact.email ||
                "";
            setAttentionContactId(siteContact.contactId);
            setSelectedAttentionContactName(contactDisplayName);
            setAttentionSearchTerm(contactDisplayName);
            // Keep clientName as company name (Client = company, Attention = contact)
            // Mark as auto-filled after setting (prevents infinite loops)
            // If companyContacts load later and match is found, the matchedContact branch will handle it
            setAttentionAutoFilled(true);
        }
    }, [
        companyContacts,
        loadedSiteData,
        attentionContactId,
        attentionAutoFilled,
    ]);
    // Filter suburbs based on search term (fallback to Matthew Proctor if not in local list)
    useEffect(() => {
        if (suburbSearchTerm.trim()) {
            searchSuburbsAsync(suburbSearchTerm).then((results) => {
                setFilteredSuburbs(results.slice(0, 50));
            });
        }
        else {
            setFilteredSuburbs([]);
        }
    }, [suburbSearchTerm]);
    // Initialize suburb search term from newSuburb
    useEffect(() => {
        if (newSuburb) {
            setSuburbSearchTerm(newSuburb);
        }
    }, [newSuburb]);
    // Handle outside clicks for suburb dropdown
    useEffect(() => {
        if (!showSuburbDropdown)
            return;
        const container = suburbDropdownRef.current;
        if (!container)
            return;
        const handleClickOutside = (event) => {
            const target = event.target;
            if (container && !container.contains(target)) {
                setShowSuburbDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [showSuburbDropdown]);
    // Update accordion data when cost parameters are loaded
    useEffect(() => {
        if (costParametersLoaded && costParameters.length > 0) {
            const defaultData = getDefaultAccordionData();
            const mappedData = mapCostParametersToAccordion(defaultData, costParameters);
            setAccordionData(mappedData);
        }
    }, [costParametersLoaded, costParameters, mapCostParametersToAccordion]);
    // Merge work items from loaded site data after cost parameters are loaded
    useEffect(() => {
        if (costParametersLoaded &&
            loadedSiteData &&
            loadedSiteData.workItems &&
            loadedSiteData.workItems.length > 0) {
            mergeWorkItems(loadedSiteData.workItems);
        }
    }, [costParametersLoaded, loadedSiteData, mergeWorkItems]);
    // Recalculate area-based items when area info changes
    useEffect(() => {
        if (!costParametersLoaded)
            return;
        setAccordionData((prevData) => {
            const newData = { ...prevData };
            // Iterate through all categories and sections to recalculate area-based items
            Object.keys(newData).forEach((categoryKey) => {
                const category = categoryKey;
                const categoryItems = [...newData[category]];
                categoryItems.forEach((item, itemIndex) => {
                    if (item.sections) {
                        const sections = [...item.sections];
                        sections.forEach((section, sectionIndex) => {
                            const items = [...section.items];
                            let hasChanges = false;
                            // Helper to check if item is After Hours
                            const isAfterHoursItem = (itemTitle) => itemTitle.toLowerCase().includes("after hours");
                            // Get section settings for calculation - check from current items array
                            const afterHoursItem = items.find((item) => item.title.toLowerCase().includes("after hours"));
                            const afterHoursEnabled = afterHoursItem?.inScope || false;
                            const shopfrontSettings = getShopfrontSettings(prevData, category, section.sectionId, costParameters);
                            // Find Float value from Cost Settings (costParameters) for "19" Electric calculation
                            const isScissorLiftSection = section.sectionId === "scissor-lift";
                            const floatCostParam = isScissorLiftSection
                                ? costParameters.find((cp) => cp.category === "preliminary_cost" &&
                                    cp.sectionId === "scissor-lift" &&
                                    cp.itemTitle?.toLowerCase().includes("float"))
                                : null;
                            const floatValue = floatCostParam?.unitCost ?? undefined;
                            // First pass: Calculate all non-After-Hours items
                            items.forEach((subItem, subItemIndex) => {
                                if (subItem.inScope && !isAfterHoursItem(subItem.title)) {
                                    const isShopfrontOver36m = subItem.title
                                        .toLowerCase()
                                        .includes("over 3.6m");
                                    // For OVER 3.6m item, calculate even without unit cost
                                    if (isShopfrontOver36m &&
                                        section.sectionId === "shopfronts") {
                                        const shopfrontItemsTotal = getShopfrontItemsTotal(items, shopfrontSettings);
                                        // Extract After Hours multiplier from the item's unitCost
                                        const afterHoursMultiplier = afterHoursEnabled && afterHoursItem?.unitCost
                                            ? parseFloat(afterHoursItem.unitCost
                                                .toString()
                                                .replace(/[^0-9.-]/g, "")) || 1.3
                                            : undefined;
                                        const calculationResult = calculateTotalCost({
                                            itemId: subItem.id,
                                            sectionId: section.sectionId,
                                            category,
                                            title: subItem.title,
                                            unitCost: 0, // Not used for OVER 3.6m
                                            areaInfo,
                                            afterHoursEnabled,
                                            afterHoursMultiplier,
                                            shopfrontItemsTotal,
                                        });
                                        const newTotalCost = formatCurrency(calculationResult.totalCost);
                                        if (newTotalCost !== subItem.totalCost) {
                                            items[subItemIndex] = {
                                                ...items[subItemIndex],
                                                totalCost: newTotalCost,
                                            };
                                        }
                                    }
                                    else if (subItem.unitCost) {
                                        // For other items, require unit cost
                                        const unitCost = parseCurrency(subItem.unitCost);
                                        if (unitCost > 0) {
                                            // Check if this is "19" Electric in scissor-lift section
                                            const is19Electric = isScissorLiftSection &&
                                                subItem.title.toLowerCase().includes("19") &&
                                                subItem.title.toLowerCase().includes("electric");
                                            // Calculate base total cost (without height factor)
                                            // Extract After Hours multiplier from the item's unitCost
                                            const afterHoursMultiplier = afterHoursEnabled && afterHoursItem?.unitCost
                                                ? parseFloat(afterHoursItem.unitCost
                                                    .toString()
                                                    .replace(/[^0-9.-]/g, "")) || 1.3
                                                : undefined;
                                            const calculationResult = calculateTotalCost({
                                                itemId: subItem.id,
                                                sectionId: section.sectionId,
                                                category,
                                                title: subItem.title,
                                                unitCost,
                                                quantity: subItem.qty
                                                    ? parseFloat(subItem.qty)
                                                    : undefined,
                                                weeksQty: subItem.weeksQty
                                                    ? parseFloat(subItem.weeksQty)
                                                    : undefined,
                                                numberQty: subItem.numberQty
                                                    ? parseFloat(subItem.numberQty)
                                                    : undefined,
                                                areaInfo,
                                                afterHoursEnabled,
                                                afterHoursMultiplier,
                                                floatValue: is19Electric ? floatValue : undefined,
                                            });
                                            // Apply height factor for shopfront items if toggle is ON
                                            let finalTotal = calculationResult.totalCost;
                                            if (section.sectionId === "shopfronts" &&
                                                shopfrontSettings.heightEnabled) {
                                                const isStandardGlazed = subItem.id === "shopfront-1" ||
                                                    subItem.title
                                                        .toLowerCase()
                                                        .includes("standard glazed") ||
                                                    subItem.title.toLowerCase().includes("standard");
                                                const isHeavyDuty = subItem.id === "shopfront-2" ||
                                                    subItem.title.toLowerCase().includes("heavy duty") ||
                                                    subItem.title.toLowerCase().includes("heavy");
                                                if (isStandardGlazed) {
                                                    finalTotal *= shopfrontSettings.stdHeightFactor;
                                                }
                                                else if (isHeavyDuty) {
                                                    finalTotal *= shopfrontSettings.hvyHeightFactor;
                                                }
                                            }
                                            const newTotalCost = formatCurrency(finalTotal);
                                            if (newTotalCost !== subItem.totalCost) {
                                                items[subItemIndex] = {
                                                    ...items[subItemIndex],
                                                    totalCost: newTotalCost,
                                                };
                                                hasChanges = true;
                                            }
                                        }
                                    }
                                }
                            });
                            // Second pass: Handle toggle-only items and shopfront-3/4 - they should NOT have a totalCost
                            items.forEach((subItem, subItemIndex) => {
                                const isAfterHoursToggle = isAfterHoursItem(subItem.title);
                                const isShopfrontHeightToggle = section.sectionId === "shopfronts" &&
                                    (subItem.id === "shopfront-3a" ||
                                        subItem.title.toLowerCase().includes("over 3.6m"));
                                const isShopfront3Or4 = subItem.id === "shopfront-3" || subItem.id === "shopfront-4";
                                // Toggle-only items (After Hours, Shopfront OVER 3.6m) and shopfront-3/4 should NOT have a totalCost
                                if (isAfterHoursToggle ||
                                    isShopfrontHeightToggle ||
                                    isShopfront3Or4) {
                                    if (subItem.totalCost !== "") {
                                        items[subItemIndex] = {
                                            ...items[subItemIndex],
                                            totalCost: "", // Empty string instead of calculating
                                        };
                                        hasChanges = true;
                                    }
                                }
                            });
                            if (hasChanges) {
                                sections[sectionIndex] = { ...section, items };
                            }
                        });
                        if (sections.some((s) => s.items.some((i) => i.totalCost))) {
                            categoryItems[itemIndex] = { ...item, sections };
                        }
                    }
                });
                newData[category] = categoryItems;
            });
            return newData;
        });
    }, [areaInfo, costParametersLoaded]);
    // Calculate summary values from all work items
    const calculatedSummary = useMemo(() => {
        let subTotal = 0;
        const categories = [
            "compliance",
            "service_trades",
            "flooring",
            "internal_demolition",
            "preliminary_cost",
        ];
        categories.forEach((category) => {
            const items = accordionData[category];
            items.forEach((accordionItem) => {
                if (accordionItem.sections) {
                    accordionItem.sections.forEach((section) => {
                        section.items.forEach((subItem) => {
                            // Exclude "After Hours Works Required" items from summary total
                            const isAfterHoursItem = subItem.title
                                ?.toLowerCase()
                                .includes("after hours");
                            if (subItem.inScope && subItem.totalCost && !isAfterHoursItem) {
                                subTotal += parseCurrency(subItem.totalCost);
                            }
                        });
                    });
                }
            });
        });
        const marginAmount = buildersMarginEnabled
            ? (subTotal * buildersMarginPercentage) / 100
            : 0;
        const total = subTotal + marginAmount;
        // Get tenancy area from areaInfo for cost per m2
        const tenancyItem = areaInfo.find((item) => item.label.toLowerCase().includes("tenancy"));
        const tenancyArea = tenancyItem?.value || 0;
        const costPerM2 = tenancyArea > 0 ? total / tenancyArea : 0;
        const depositAmountRaw = depositEnabled
            ? (total * depositPercentage) / 100
            : 0;
        return {
            subTotal: formatCurrency(subTotal),
            buildersMarginAmount: formatCurrency(marginAmount),
            total: formatCurrency(total),
            costPerM2: formatCurrency(costPerM2),
            rawSubTotal: subTotal,
            rawTotal: total,
            rawCostPerM2: costPerM2,
            rawMarginAmount: marginAmount,
            depositAmount: formatCurrency(depositAmountRaw),
            rawDepositAmount: depositAmountRaw,
        };
    }, [
        accordionData,
        buildersMarginEnabled,
        buildersMarginPercentage,
        areaInfo,
        depositEnabled,
        depositPercentage,
    ]);
    const basePath = useMemo(() => (location.pathname.startsWith("/staff") ? "/staff" : "/admin"), [location.pathname]);
    const handleBack = () => {
        navigate(-1);
    };
    const renderPageHeader = () => (_jsx(_Fragment, { children: _jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }) }));
    const handleAreaInfoChange = (index, value) => {
        const updated = [...areaInfo];
        updated[index] = { ...updated[index], value };
        setAreaInfo(updated);
    };
    // Collect all work items from accordion data for saving
    const collectWorkItems = useCallback(() => {
        const workItems = [];
        let sortOrder = 0;
        Object.entries(accordionData).forEach(([category, items]) => {
            if (category === "summary")
                return;
            items.forEach((item) => {
                item.sections?.forEach((section) => {
                    section.items.forEach((subItem) => {
                        // Determine quantity based on sectionId for consistency
                        let quantity = undefined;
                        if (section.sectionId === "scissor-lift" && subItem.weeksQty) {
                            quantity = parseFloat(subItem.weeksQty);
                        }
                        else if (section.sectionId === "skip-hire" && subItem.numberQty) {
                            quantity = parseFloat(subItem.numberQty);
                        }
                        else if (subItem.qty) {
                            quantity = parseFloat(subItem.qty);
                        }
                        workItems.push({
                            category,
                            sectionId: section.sectionId,
                            sectionTitle: section.sectionTitle,
                            parentTitle: item.title,
                            itemId: subItem.id,
                            itemTitle: subItem.title,
                            inScope: subItem.inScope,
                            quantity: quantity,
                            weeksQty: subItem.weeksQty
                                ? parseFloat(subItem.weeksQty)
                                : undefined,
                            numberQty: subItem.numberQty
                                ? parseFloat(subItem.numberQty)
                                : undefined,
                            unitCost: subItem.unitCost
                                ? parseCurrency(subItem.unitCost)
                                : undefined,
                            totalCost: subItem.totalCost
                                ? parseCurrency(subItem.totalCost)
                                : undefined,
                            notes: subItem.notes,
                            isDropdown: subItem.isDropdown,
                            dropdownOptions: subItem.dropdownOptions,
                            selectedOption: subItem.selectedOption,
                            sortOrder: sortOrder++,
                        });
                    });
                });
            });
        });
        return workItems;
    }, [accordionData]);
    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);
            const lineItems = collectWorkItems();
            // Validate Estimation Name
            if (!quoteName || quoteName.trim() === "") {
                showError("Please enter an Estimation Name");
                setSaving(false);
                return;
            }
            // Validate Client Name - show toast instead of errorBanner
            if (!selectedCompanyName || selectedCompanyName.trim() === "") {
                showError("Please enter a client name");
                setSaving(false);
                return;
            }
            // Validate Attention field is mandatory
            if (!attentionContactId || attentionContactId.trim() === "") {
                showError("Please select an Attention contact");
                setSaving(false);
                return;
            }
            if (commencementDate && isWeekend(commencementDate)) {
                showError("Commencement date cannot fall on a weekend.");
                setSaving(false);
                return;
            }
            if (!contactId && !isNewSite) {
                setError("Please select a client");
                return;
            }
            let finalSiteId = siteId;
            // If creating a new site, create it first
            if (isNewSite) {
                if (!newSiteName.trim()) {
                    showError("Please enter a site name");
                    setSaving(false);
                    return;
                }
                const siteData = {
                    siteName: newSiteName,
                    tenancyNumber: newTenancyNumber || undefined,
                    tenancyName: newTenancyName || undefined,
                    buildingTypes: newBuildingType ? [newBuildingType] : undefined,
                    landlord: newLandlord || undefined,
                    address: newAddress || undefined,
                    suburb: newSuburb || undefined,
                    state: newState || undefined,
                    postcode: newPostcode || undefined,
                    companyName: newSiteCompanyName || undefined,
                    contactPersonId: attentionContactId || undefined,
                    areaInfo: areaInfo,
                    shoppingCentreId: shoppingCentreId.trim()
                        ? shoppingCentreId.trim()
                        : undefined,
                };
                const siteResponse = await sitesService.createSite(siteData);
                finalSiteId = siteResponse.data.id;
                setSiteId(finalSiteId);
                // Persist work items to the new site (same as SiteDetailsTab)
                const workItemsForSite = lineItems.map(({ category, sectionId, sectionTitle, itemId, itemTitle, inScope, quantity, unitCost, totalCost, notes, isDropdown, dropdownOptions, selectedOption, sortOrder, }) => ({
                    category,
                    sectionId,
                    sectionTitle,
                    itemId,
                    itemTitle,
                    inScope,
                    quantity,
                    unitCost,
                    totalCost,
                    notes,
                    isDropdown,
                    dropdownOptions,
                    selectedOption,
                    sortOrder,
                }));
                await sitesService.updateWorkItems(finalSiteId, workItemsForSite);
            }
            // Build address from address fields (works for both new site and existing site)
            const addressParts = [];
            if (newAddress)
                addressParts.push(newAddress);
            if (newSuburb)
                addressParts.push(newSuburb);
            if (newState)
                addressParts.push(newState);
            if (newPostcode)
                addressParts.push(newPostcode);
            const concatenatedAddress = addressParts.length > 0 ? addressParts.join(", ") : undefined;
            const createData = {
                // Keep the primary company contact - never overwrite with attention contact
                contactId: contactId,
                // Set attention contact separately - this determines who sees the quote in portal
                attentionContactId: attentionContactId || undefined,
                // Persist company name as Client (not attention contact's name)
                clientName: selectedCompanyName || clientName || undefined,
                siteId: finalSiteId || undefined,
                title: quoteName,
                description: quoteDescription,
                defitType: defitType,
                status: "inprogress",
                commencementDate: commencementDate || undefined,
                commencementTime: commencementTime || undefined,
                completionDate: completionDate || undefined,
                completionTime: completionTime || undefined,
                projectDuration: projectDuration
                    ? parseInt(projectDuration, 10)
                    : undefined,
                areaInfo,
                lineItems,
                subTotal: calculatedSummary.rawSubTotal,
                totalAmount: calculatedSummary.rawTotal,
                costPerM2: calculatedSummary.rawCostPerM2,
                buildersMarginEnabled,
                buildersMarginPercentage,
                buildersMarginAmount: calculatedSummary.rawMarginAmount,
                includeNewClientPaymentTerms: includeNewClientTerms,
                depositEnabled,
                depositPercentage: depositEnabled ? depositPercentage : undefined,
                address: concatenatedAddress,
                shoppingCentreId: shoppingCentreId.trim() || "",
            };
            await quotesService.createQuote(createData);
            // Navigate back to the quote list after creation
            navigate(`${basePath}/estimating`);
        }
        catch (err) {
            const msg = err &&
                typeof err === "object" &&
                "response" in err &&
                err.response &&
                typeof err.response.data ===
                    "object"
                ? err.response.data?.message
                : null;
            setError(msg || "Failed to create quote");
            console.error("Error creating quote:", err);
        }
        finally {
            setSaving(false);
        }
    };
    // Accordion handlers
    const toggleAccordion = (id) => {
        setExpandedAccordions((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            }
            else {
                newSet.add(id);
            }
            return newSet;
        });
    };
    const handleExpandAll = () => {
        const currentItems = accordionData[activeWorkCategory];
        const allIds = currentItems.map((item) => item.id);
        setExpandedAccordions(new Set(allIds));
    };
    const handleCollapseAll = () => {
        setExpandedAccordions(new Set());
    };
    // Helper function to recalculate all items in a section (handles After Hours correctly)
    const recalculateSectionItems = (items, sectionId, category) => {
        const isAfterHoursItem = (itemTitle) => itemTitle.toLowerCase().includes("after hours");
        // Check After Hours status directly from items array (not from prevData which has stale state)
        const afterHoursItem = items.find((item) => item.title.toLowerCase().includes("after hours"));
        const afterHoursEnabled = afterHoursItem?.inScope || false;
        // Extract After Hours multiplier from the item's unitCost (defaults to 1.3 if not set)
        const afterHoursMultiplier = afterHoursEnabled && afterHoursItem?.unitCost
            ? parseFloat(afterHoursItem.unitCost.toString().replace(/[^0-9.-]/g, "")) || 1.3
            : undefined;
        const shopfrontSettings = getShopfrontSettingsFromItems(items, costParameters);
        const updatedItems = [...items];
        // Find Float value from Cost Settings (costParameters) for "19" Electric calculation
        const isScissorLiftSection = sectionId === "scissor-lift";
        const floatCostParam = isScissorLiftSection
            ? costParameters.find((cp) => cp.category === "preliminary_cost" &&
                cp.sectionId === "scissor-lift" &&
                cp.itemTitle?.toLowerCase().includes("float"))
            : null;
        const floatValue = floatCostParam?.unitCost ?? undefined;
        // For shopfronts section, process shopfront-1 and shopfront-2 first to ensure they're recalculated
        // before the OVER 3.6m item (which depends on their totals)
        if (sectionId === "shopfronts") {
            updatedItems.forEach((subItem, subItemIndex) => {
                const isStandardGlazed = subItem.id === "shopfront-1" ||
                    subItem.title.toLowerCase().includes("standard glazed") ||
                    subItem.title.toLowerCase().includes("standard");
                const isHeavyDuty = subItem.id === "shopfront-2" ||
                    subItem.title.toLowerCase().includes("heavy duty") ||
                    subItem.title.toLowerCase().includes("heavy");
                // Recalculate shopfront-1 and shopfront-2 if they're inScope and have unitCost
                if ((isStandardGlazed || isHeavyDuty) &&
                    subItem.inScope &&
                    subItem.unitCost) {
                    const unitCost = parseCurrency(subItem.unitCost);
                    if (unitCost > 0) {
                        // Calculate base total cost (without height factor)
                        const calculationResult = calculateTotalCost({
                            itemId: subItem.id,
                            sectionId,
                            category,
                            title: subItem.title,
                            unitCost,
                            quantity: subItem.qty ? parseFloat(subItem.qty) : undefined,
                            areaInfo,
                            afterHoursEnabled,
                            afterHoursMultiplier,
                        });
                        // Apply height factor only if toggle is ON
                        let finalTotal = calculationResult.totalCost;
                        if (shopfrontSettings.heightEnabled) {
                            if (isStandardGlazed) {
                                finalTotal *= shopfrontSettings.stdHeightFactor;
                            }
                            else if (isHeavyDuty) {
                                finalTotal *= shopfrontSettings.hvyHeightFactor;
                            }
                        }
                        updatedItems[subItemIndex] = {
                            ...updatedItems[subItemIndex],
                            totalCost: formatCurrency(finalTotal),
                        };
                    }
                }
            });
        }
        // First pass: Calculate all non-After-Hours items
        updatedItems.forEach((subItem, subItemIndex) => {
            if (subItem.inScope && !isAfterHoursItem(subItem.title)) {
                const isShopfrontOver36m = subItem.title
                    .toLowerCase()
                    .includes("over 3.6m");
                // For OVER 3.6m item, calculate even without unit cost
                if (isShopfrontOver36m && sectionId === "shopfronts") {
                    const shopfrontItemsTotal = getShopfrontItemsTotal(updatedItems, shopfrontSettings);
                    const calculationResult = calculateTotalCost({
                        itemId: subItem.id,
                        sectionId,
                        category,
                        title: subItem.title,
                        unitCost: 0, // Not used for OVER 3.6m
                        areaInfo,
                        afterHoursEnabled,
                        afterHoursMultiplier,
                        shopfrontItemsTotal,
                    });
                    updatedItems[subItemIndex] = {
                        ...updatedItems[subItemIndex],
                        totalCost: formatCurrency(calculationResult.totalCost),
                    };
                }
                else if (subItem.unitCost) {
                    // For other items, require unit cost
                    const unitCost = parseCurrency(subItem.unitCost);
                    if (unitCost > 0) {
                        // Skip shopfront-1 and shopfront-2 if we already processed them above
                        const isStandardGlazed = subItem.id === "shopfront-1" ||
                            subItem.title.toLowerCase().includes("standard glazed") ||
                            subItem.title.toLowerCase().includes("standard");
                        const isHeavyDuty = subItem.id === "shopfront-2" ||
                            subItem.title.toLowerCase().includes("heavy duty") ||
                            subItem.title.toLowerCase().includes("heavy");
                        // If this is shopfront-1 or shopfront-2 and we're in shopfronts section, skip (already processed)
                        if (sectionId === "shopfronts" &&
                            (isStandardGlazed || isHeavyDuty)) {
                            // Already processed above, skip
                        }
                        else {
                            // Check if this is "19" Electric in scissor-lift section
                            const is19Electric = isScissorLiftSection &&
                                subItem.title.toLowerCase().includes("19") &&
                                subItem.title.toLowerCase().includes("electric");
                            // Calculate base total cost (without height factor)
                            const calculationResult = calculateTotalCost({
                                itemId: subItem.id,
                                sectionId,
                                category,
                                title: subItem.title,
                                unitCost,
                                quantity: subItem.qty ? parseFloat(subItem.qty) : undefined,
                                weeksQty: subItem.weeksQty
                                    ? parseFloat(subItem.weeksQty)
                                    : undefined,
                                numberQty: subItem.numberQty
                                    ? parseFloat(subItem.numberQty)
                                    : undefined,
                                areaInfo,
                                afterHoursEnabled,
                                afterHoursMultiplier,
                                floatValue: is19Electric ? floatValue : undefined,
                            });
                            // Apply height factor for shopfront items if toggle is ON
                            let finalTotal = calculationResult.totalCost;
                            if (sectionId === "shopfronts" &&
                                shopfrontSettings.heightEnabled) {
                                if (isStandardGlazed) {
                                    finalTotal *= shopfrontSettings.stdHeightFactor;
                                }
                                else if (isHeavyDuty) {
                                    finalTotal *= shopfrontSettings.hvyHeightFactor;
                                }
                            }
                            updatedItems[subItemIndex] = {
                                ...updatedItems[subItemIndex],
                                totalCost: formatCurrency(finalTotal),
                            };
                        }
                    }
                }
            }
            else if (!subItem.inScope) {
                // Clear total cost when not in scope (but keep for After Hours items)
                if (!isAfterHoursItem(subItem.title)) {
                    updatedItems[subItemIndex] = {
                        ...updatedItems[subItemIndex],
                        totalCost: "",
                    };
                }
            }
        });
        // Second pass: Calculate After Hours items using updated totals from first pass
        updatedItems.forEach((subItem, subItemIndex) => {
            if (subItem.inScope &&
                subItem.unitCost &&
                isAfterHoursItem(subItem.title)) {
                // For ALL After Hours items, calculate normally using unit cost from cost settings
                // NO 30% calculation - all After Hours items use their unit cost
                const unitCost = parseCurrency(subItem.unitCost);
                if (unitCost > 0) {
                    // Calculate shopfront items total for OVER 3.6m item (if applicable)
                    const isShopfrontOver36m = subItem.title
                        .toLowerCase()
                        .includes("over 3.6m");
                    const shopfrontItemsTotal = isShopfrontOver36m && sectionId === "shopfronts"
                        ? getShopfrontItemsTotal(updatedItems, shopfrontSettings)
                        : undefined;
                    const calculationResult = calculateTotalCost({
                        itemId: subItem.id,
                        sectionId,
                        category,
                        title: subItem.title,
                        unitCost, // Unit cost from cost settings
                        areaInfo,
                        afterHoursEnabled: false, // After Hours item itself doesn't use the factor
                        shopfrontItemsTotal,
                    });
                    updatedItems[subItemIndex] = {
                        ...updatedItems[subItemIndex],
                        totalCost: formatCurrency(calculationResult.totalCost),
                    };
                }
            }
            else if (!subItem.inScope && isAfterHoursItem(subItem.title)) {
                updatedItems[subItemIndex] = {
                    ...updatedItems[subItemIndex],
                    totalCost: "",
                };
            }
        });
        return updatedItems;
    };
    const toggleSubItemScope = (itemId, sectionIndex, subItemId) => {
        setAccordionData((prevData) => {
            const newData = { ...prevData };
            const categoryItems = [...newData[activeWorkCategory]];
            const itemIndex = categoryItems.findIndex((item) => item.id === itemId);
            if (itemIndex === -1)
                return prevData;
            const item = { ...categoryItems[itemIndex] };
            if (!item.sections)
                return prevData;
            const sections = [...item.sections];
            const section = { ...sections[sectionIndex] };
            const items = [...section.items];
            const subItemIndex = items.findIndex((sub) => sub.id === subItemId);
            if (subItemIndex === -1)
                return prevData;
            const toggledItem = items[subItemIndex];
            const newInScope = !toggledItem.inScope;
            const isAfterHoursItem = toggledItem.title
                .toLowerCase()
                .includes("after hours");
            // Preserve edited unitCost for After Hours items when toggling
            // Only set default from Cost Settings if unitCost is empty
            let updatedUnitCost = toggledItem.unitCost;
            if (isAfterHoursItem) {
                if (newInScope) {
                    // Toggling to Yes - only set default if unitCost is empty
                    if (!toggledItem.unitCost || toggledItem.unitCost.trim() === "") {
                        // First try to use defaultUnitCost if available
                        if (toggledItem.defaultUnitCost != null) {
                            updatedUnitCost = `${toggledItem.defaultUnitCost}`;
                        }
                        // If not available, look up from costParameters
                        else {
                            const costParam = costParameters.find((cp) => cp.sectionId === section.sectionId &&
                                cp.itemTitle
                                    ?.toLowerCase()
                                    .includes("after hours works required"));
                            if (costParam?.unitCost != null) {
                                updatedUnitCost = `${costParam.unitCost}`;
                                // Also store it as defaultUnitCost for future use
                                toggledItem.defaultUnitCost = costParam.unitCost;
                            }
                        }
                    }
                    // If unitCost already has a value, preserve it (don't overwrite edited values)
                }
                else {
                    // Toggling to No - preserve the value (don't clear it)
                    // This allows admins to keep their edited rate even when toggled off
                    updatedUnitCost = toggledItem.unitCost || "";
                }
            }
            items[subItemIndex] = {
                ...toggledItem,
                inScope: newInScope,
                unitCost: updatedUnitCost,
            };
            // Check if this is the shopfront OVER 3.6m toggle
            const isShopfrontOver36mToggle = subItemId === "shopfront-3a" ||
                toggledItem.title?.toLowerCase().includes("over 3.6m");
            // Handle shopfront-3a toggle: load shopfront-3 and shopfront-4 defaults when toggled YES
            if (isShopfrontOver36mToggle && section.sectionId === "shopfronts") {
                if (newInScope) {
                    // Toggling to YES - load shopfront-3 and shopfront-4 defaults from costsettings if not already stored
                    const shopfront3Item = items.find((item) => item.id === "shopfront-3");
                    const shopfront4Item = items.find((item) => item.id === "shopfront-4");
                    // Load shopfront-3 default if not already stored
                    if (shopfront3Item &&
                        (!shopfront3Item.unitCost || shopfront3Item.unitCost.trim() === "")) {
                        const shopfront3CostParam = costParameters.find((cp) => cp.itemId === "shopfront-3" ||
                            cp.itemTitle
                                ?.toLowerCase()
                                .includes("shopfront height factor std"));
                        if (shopfront3CostParam?.unitCost != null) {
                            const shopfront3Index = items.findIndex((item) => item.id === "shopfront-3");
                            if (shopfront3Index !== -1) {
                                items[shopfront3Index] = {
                                    ...items[shopfront3Index],
                                    unitCost: `${shopfront3CostParam.unitCost}`,
                                };
                            }
                        }
                    }
                    // Load shopfront-4 default if not already stored
                    if (shopfront4Item &&
                        (!shopfront4Item.unitCost || shopfront4Item.unitCost.trim() === "")) {
                        const shopfront4CostParam = costParameters.find((cp) => cp.itemId === "shopfront-4" ||
                            cp.itemTitle
                                ?.toLowerCase()
                                .includes("shopfront height factor hvy"));
                        if (shopfront4CostParam?.unitCost != null) {
                            const shopfront4Index = items.findIndex((item) => item.id === "shopfront-4");
                            if (shopfront4Index !== -1) {
                                items[shopfront4Index] = {
                                    ...items[shopfront4Index],
                                    unitCost: `${shopfront4CostParam.unitCost}`,
                                };
                            }
                        }
                    }
                }
            }
            // Always recalculate section items when any toggle changes
            // This is especially important when the shopfront OVER 3.6m toggle (shopfront-3a) changes,
            // as it ensures shopfront-1 and shopfront-2 are recalculated with correct height factor.
            // Also handles After Hours items correctly.
            const recalculatedItems = recalculateSectionItems(items, section.sectionId, activeWorkCategory);
            section.items = recalculatedItems;
            sections[sectionIndex] = section;
            item.sections = sections;
            const isScissorLiftToggle = subItemId === "scissor-1";
            // If the scissor-lift toggle changed, also recalculate the scissor-lift section
            // so that the \"19\" Electric item is always up to date (including float value)
            if (isScissorLiftToggle && section.sectionId === "scissor-lift") {
                const scissorLiftItemIndex = categoryItems.findIndex((catItem) => catItem.sections?.some((s) => s.sectionId === "scissor-lift"));
                if (scissorLiftItemIndex !== -1) {
                    const scissorLiftItem = { ...categoryItems[scissorLiftItemIndex] };
                    if (scissorLiftItem.sections) {
                        const scissorLiftSections = [...scissorLiftItem.sections];
                        const scissorLiftSectionIndex = scissorLiftSections.findIndex((s) => s.sectionId === "scissor-lift");
                        if (scissorLiftSectionIndex !== -1) {
                            const scissorLiftSection = {
                                ...scissorLiftSections[scissorLiftSectionIndex],
                            };
                            const scissorLiftItems = [...scissorLiftSection.items];
                            const recalculatedScissorLiftItems = recalculateSectionItems(scissorLiftItems, "scissor-lift", activeWorkCategory);
                            scissorLiftSection.items = recalculatedScissorLiftItems;
                            scissorLiftSections[scissorLiftSectionIndex] = scissorLiftSection;
                            scissorLiftItem.sections = scissorLiftSections;
                            categoryItems[scissorLiftItemIndex] = scissorLiftItem;
                        }
                    }
                }
            }
            categoryItems[itemIndex] = item;
            newData[activeWorkCategory] = categoryItems;
            return newData;
        });
    };
    const updateSubItemField = (itemId, sectionIndex, subItemId, field, value) => {
        setAccordionData((prevData) => {
            const newData = { ...prevData };
            const categoryItems = [...newData[activeWorkCategory]];
            const itemIndex = categoryItems.findIndex((item) => item.id === itemId);
            if (itemIndex === -1)
                return prevData;
            const item = { ...categoryItems[itemIndex] };
            if (!item.sections)
                return prevData;
            const sections = [...item.sections];
            const section = { ...sections[sectionIndex] };
            const items = [...section.items];
            const subItemIndex = items.findIndex((sub) => sub.id === subItemId);
            if (subItemIndex === -1)
                return prevData;
            // Update the field
            const updatedSubItem = { ...items[subItemIndex], [field]: value };
            items[subItemIndex] = updatedSubItem;
            // If updating shopfront-3 or shopfront-4 unitCost, trigger recalculation of dependent items
            const isShopfront3Or4 = subItemId === "shopfront-3" || subItemId === "shopfront-4";
            if (isShopfront3Or4 &&
                field === "unitCost" &&
                section.sectionId === "shopfronts") {
                // Recalculate shopfront-1 and shopfront-2 with new height factors
                // The items array already has the updated shopfront-3/4 value
                const recalculatedItems = recalculateSectionItems(items, section.sectionId, activeWorkCategory);
                // Replace items with recalculated items, but preserve our updated shopfront-3/4 value
                recalculatedItems.forEach((recalculatedItem, index) => {
                    if (recalculatedItem.id === subItemId) {
                        // Preserve the updated value we just set (don't let recalculation overwrite it)
                        items[index] = updatedSubItem;
                    }
                    else {
                        items[index] = recalculatedItem;
                    }
                });
                section.items = items;
                sections[sectionIndex] = section;
                item.sections = sections;
                // Also recalculate scissor-lift section if shopfront-3a is enabled
                const shopfront3aItem = items.find((item) => item.id === "shopfront-3a");
                if (shopfront3aItem?.inScope) {
                    const scissorLiftItemIndex = categoryItems.findIndex((catItem) => catItem.sections?.some((s) => s.sectionId === "scissor-lift"));
                    if (scissorLiftItemIndex !== -1) {
                        const scissorLiftItem = { ...categoryItems[scissorLiftItemIndex] };
                        if (scissorLiftItem.sections) {
                            const scissorLiftSections = [...scissorLiftItem.sections];
                            const scissorLiftSectionIndex = scissorLiftSections.findIndex((s) => s.sectionId === "scissor-lift");
                            if (scissorLiftSectionIndex !== -1) {
                                const scissorLiftSection = {
                                    ...scissorLiftSections[scissorLiftSectionIndex],
                                };
                                const scissorLiftItems = [...scissorLiftSection.items];
                                const recalculatedScissorLiftItems = recalculateSectionItems(scissorLiftItems, "scissor-lift", activeWorkCategory);
                                scissorLiftSection.items = recalculatedScissorLiftItems;
                                scissorLiftSections[scissorLiftSectionIndex] =
                                    scissorLiftSection;
                                scissorLiftItem.sections = scissorLiftSections;
                                categoryItems[scissorLiftItemIndex] = scissorLiftItem;
                            }
                        }
                    }
                }
            }
            // If updating shopfront-1 or shopfront-2 totalCost, recalculate OVER 3.6m item
            if (field === "totalCost" &&
                section.sectionId === "shopfronts" &&
                (updatedSubItem.id === "shopfront-1" ||
                    updatedSubItem.id === "shopfront-2")) {
                const over36mItem = items.find((item) => item.title.toLowerCase().includes("over 3.6m") && item.inScope);
                if (over36mItem) {
                    // Check from current items array
                    const afterHoursItem = items.find((item) => item.title.toLowerCase().includes("after hours"));
                    const afterHoursEnabled = afterHoursItem?.inScope || false;
                    const shopfrontSettings = getShopfrontSettingsFromItems(items, costParameters);
                    const shopfrontItemsTotal = getShopfrontItemsTotal(items, shopfrontSettings);
                    // Extract After Hours multiplier from the item's unitCost
                    const afterHoursMultiplier = afterHoursEnabled && afterHoursItem?.unitCost
                        ? parseFloat(afterHoursItem.unitCost.toString().replace(/[^0-9.-]/g, "")) || 1.3
                        : undefined;
                    const calculationResult = calculateTotalCost({
                        itemId: over36mItem.id,
                        sectionId: section.sectionId,
                        category: activeWorkCategory,
                        title: over36mItem.title,
                        unitCost: 0,
                        areaInfo,
                        afterHoursEnabled,
                        afterHoursMultiplier,
                        shopfrontItemsTotal,
                    });
                    const over36mIndex = items.findIndex((item) => item.id === over36mItem.id);
                    if (over36mIndex !== -1) {
                        items[over36mIndex] = {
                            ...over36mItem,
                            totalCost: formatCurrency(calculationResult.totalCost),
                        };
                    }
                }
            }
            // Recalculate all items in the section (handles After Hours correctly)
            const recalculatedItems = recalculateSectionItems(items, section.sectionId, activeWorkCategory);
            section.items = recalculatedItems;
            sections[sectionIndex] = section;
            item.sections = sections;
            categoryItems[itemIndex] = item;
            newData[activeWorkCategory] = categoryItems;
            return newData;
        });
    };
    // Render Summary tab content
    const renderSummaryContent = () => (_jsxs("div", { className: styles.summaryContainer, children: [_jsxs("div", { className: styles.summaryRow, children: [_jsx("div", { className: styles.summaryLabel, children: "Sub Total" }), _jsx("div", { className: styles.summaryValue, children: calculatedSummary.subTotal })] }), _jsxs("div", { className: styles.summaryMarginRow, children: [_jsxs("div", { className: styles.summaryMarginLeft, children: [_jsx("span", { className: styles.summaryMarginLabel, children: "Builders Preliminaries & Margin" }), _jsx("button", { className: `${styles.toggleButton} ${buildersMarginEnabled ? styles.yes : styles.no}`, onClick: () => setBuildersMarginEnabled(!buildersMarginEnabled), children: buildersMarginEnabled ? "YES" : "NO" })] }), _jsxs("div", { className: styles.summaryMarginRight, children: [_jsx("input", { type: "number", className: styles.marginPercentInput, value: buildersMarginPercentage, onChange: (e) => setBuildersMarginPercentage(Number(e.target.value) || 0), disabled: !buildersMarginEnabled, min: "0", max: "100" }), _jsx("span", { className: styles.percentSign, children: "%" }), _jsx("span", { className: styles.summaryMarginAmount, children: calculatedSummary.buildersMarginAmount })] })] }), _jsxs("div", { className: styles.summaryRow, children: [_jsx("div", { className: styles.summaryLabel, children: "Total" }), _jsx("div", { className: styles.summaryValue, children: calculatedSummary.total })] }), _jsxs("div", { className: styles.summaryRow, children: [_jsx("div", { className: styles.summaryLabel, children: "Cost per m2" }), _jsx("div", { className: styles.summaryValue, children: calculatedSummary.costPerM2 })] }), _jsxs("div", { className: styles.paymentTermsSection, children: [_jsx("h4", { className: styles.paymentTermsTitle, children: "PAYMENT TERMS" }), _jsxs("div", { className: styles.paymentTermsRow, children: [_jsx("span", { className: styles.paymentTermsLabel, children: "Include new client payment terms" }), _jsx("button", { className: `${styles.toggleButton} ${includeNewClientTerms ? styles.yes : styles.no}`, onClick: () => setIncludeNewClientTerms(!includeNewClientTerms), children: includeNewClientTerms ? "YES" : "NO" })] }), includeNewClientTerms && (_jsxs("div", { className: styles.depositSection, children: [_jsx("h5", { className: styles.depositTitle, children: "Deposit" }), _jsxs("div", { className: styles.depositRow, children: [_jsx(Checkbox, { checked: depositEnabled, onChange: (checked) => setDepositEnabled(checked), label: "Amount", size: "md" }), _jsxs("div", { className: styles.depositInputWrapper, children: [_jsx("input", { type: "number", className: styles.depositInput, value: depositPercentage, onChange: (e) => setDepositPercentage(Number(e.target.value) || 0), disabled: !depositEnabled, min: "0", max: "100" }), _jsx("span", { className: styles.percentSign, children: "%" })] })] }), _jsxs("div", { className: styles.depositAmountRow, children: [_jsxs("span", { className: styles.depositAmountLabel, children: ["Deposit amount (", depositPercentage, "%)"] }), _jsx("span", { className: styles.depositAmountValue, children: calculatedSummary.depositAmount })] })] }))] })] }));
    // Render accordion content for work categories
    const renderAccordionContent = () => {
        if (activeWorkCategory === "summary") {
            return renderSummaryContent();
        }
        const items = accordionData[activeWorkCategory];
        return (_jsx("div", { className: styles.accordionList, children: items.map((item) => (_jsxs("div", { className: styles.accordionItem, children: [_jsx("button", { className: `${styles.accordionHeader} ${expandedAccordions.has(item.id) ? styles.expanded : ""}`, onClick: () => toggleAccordion(item.id), children: _jsxs("div", { className: styles.accordionTitleSection, children: [_jsx("svg", { className: styles.accordionChevron, width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M6 9l6 6 6-6" }) }), _jsx("span", { className: styles.accordionTitle, children: item.title })] }) }), expandedAccordions.has(item.id) && (_jsx("div", { className: styles.accordionBody, children: item.sections ? (_jsx("div", { className: styles.subItemsTable, children: item.sections.map((section, sectionIndex) => (_jsxs("div", { className: styles.sectionBlock, children: [section.sectionTitle && (_jsx("div", { className: styles.sectionTitleRow, children: _jsx("span", { className: styles.sectionTitleText, children: section.sectionTitle }) })), !section.sectionTitle && (_jsxs("div", { className: section.showQty ||
                                            section.showWeeksQty ||
                                            section.showNumberQty
                                            ? styles.tableHeaderWithQty
                                            : styles.tableHeader, children: [_jsx("div", { className: styles.tableHeaderCell }), _jsx("div", { className: styles.tableHeaderCell, children: "In Scope" }), section.showQty && (_jsx("div", { className: styles.tableHeaderCell, children: "Qty" })), section.showWeeksQty && (_jsx("div", { className: styles.tableHeaderCell, children: "# of Weeks" })), section.showNumberQty && (_jsx("div", { className: styles.tableHeaderCell, children: "No." })), _jsx("div", { className: styles.tableHeaderCell, children: "Unit Cost" }), _jsx("div", { className: styles.tableHeaderCell, children: "Total Cost" }), _jsx("div", { className: styles.tableHeaderCell, children: "Notes" })] })), section.items
                                        .filter((subItem) => {
                                        // Filter out shopfront-3 and shopfront-4 - they don't have rows
                                        if (subItem.id === "shopfront-3" ||
                                            subItem.id === "shopfront-4") {
                                            return false;
                                        }
                                        const title = subItem.title.toLowerCase();
                                        return (!title.includes("shopfront height factor std") &&
                                            !title.includes("shopfront height factor hvy"));
                                    })
                                        .map((subItem) => section.isDropdownSection ? (_jsxs("div", { className: styles.tableRowDropdown, children: [_jsx("div", { className: styles.tableCellDropdown, children: _jsxs("div", { className: styles.dropdownFieldGroup, children: [_jsx("label", { className: styles.dropdownLabel, children: subItem.title }), _jsx("select", { className: styles.dropdownSelect, value: subItem.selectedOption || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "selectedOption", e.target.value), children: subItem.dropdownOptions?.map((option) => (_jsx("option", { value: option, children: option }, option))) })] }) }), _jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.costInput, value: subItem.totalCost || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "totalCost", e.target.value), readOnly: !canEditPrices }) }), _jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.notesInput, value: subItem.notes || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "notes", e.target.value) }) })] }, subItem.id)) : (_jsxs("div", { className: section.showQty ||
                                            section.showWeeksQty ||
                                            section.showNumberQty
                                            ? styles.tableRowWithQty
                                            : styles.tableRow, children: [_jsx("div", { className: styles.tableCell, children: _jsx("span", { className: styles.subItemTitle, children: subItem.title }) }), _jsx("div", { className: styles.tableCell, children: _jsx("button", { className: `${styles.toggleButton} ${subItem.inScope ? styles.yes : styles.no}`, onClick: () => toggleSubItemScope(item.id, sectionIndex, subItem.id), children: subItem.inScope ? "YES" : "NO" }) }), section.showQty && (_jsx("div", { className: styles.tableCell, children: !subItem.hideQty && (_jsx("input", { type: "text", className: styles.qtyInput, value: subItem.qty || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "qty", e.target.value) })) })), section.showWeeksQty && (_jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.qtyInput, value: subItem.weeksQty || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "weeksQty", e.target.value) }) })), section.showNumberQty && (_jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.qtyInput, value: subItem.numberQty || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "numberQty", e.target.value) }) })), (() => {
                                                const isShopfrontOver36m = subItem.id === "shopfront-3a" ||
                                                    subItem.title
                                                        .toLowerCase()
                                                        .includes("over 3.6m");
                                                if (isShopfrontOver36m) {
                                                    // For shopfront-3a, display shopfront-3's unitCost in the unitCost field
                                                    // Get shopfront-3 item from the section
                                                    const shopfront3Item = section.items.find((item) => item.id === "shopfront-3");
                                                    const shopfront3Value = shopfront3Item?.unitCost || "";
                                                    // Only show input if toggle is YES
                                                    if (subItem.inScope) {
                                                        return (_jsxs("div", { className: `${styles.tableCell} ${styles.tableCellWithLabel}`, children: [_jsx("label", { children: shopfront3Item?.title }), _jsx("input", { type: "text", className: styles.costInput, value: shopfront3Value, onChange: (e) => {
                                                                        // Update shopfront-3's unitCost when user edits
                                                                        const shopfront3Index = section.items.findIndex((item) => item.id === "shopfront-3");
                                                                        if (shopfront3Index !== -1) {
                                                                            const normalizedValue = e.target.value
                                                                                .replace(/,/g, ".")
                                                                                .replace(/[^0-9.-]/g, "");
                                                                            const parts = normalizedValue.split(".");
                                                                            const finalValue = parts.length > 2
                                                                                ? parts[0] +
                                                                                    "." +
                                                                                    parts.slice(1).join("")
                                                                                : normalizedValue;
                                                                            updateSubItemField(item.id, sectionIndex, "shopfront-3", "unitCost", finalValue);
                                                                        }
                                                                    }, readOnly: !canEditPrices })] }));
                                                    }
                                                    else {
                                                        return (_jsx("div", { className: styles.tableCell }));
                                                    }
                                                }
                                                return (_jsx("div", { className: styles.tableCell, children: (() => {
                                                        const isDecimalInputItem = (title) => {
                                                            const lower = title.toLowerCase();
                                                            return (lower.includes("shopfront height factor std") ||
                                                                lower.includes("shopfront height factor hvy") ||
                                                                lower.includes("after hours"));
                                                        };
                                                        const isDecimalItem = isDecimalInputItem(subItem.title);
                                                        // For decimal items, preserve raw string value to allow decimal input
                                                        // Only format when displaying a finalized number (not during active editing)
                                                        const displayValue = isDecimalItem
                                                            ? subItem.unitCost || ""
                                                            : subItem.unitCost || "";
                                                        return (_jsx("input", { type: "text", className: styles.costInput, value: displayValue, onChange: (e) => {
                                                                let normalizedValue = e.target.value;
                                                                if (isDecimalItem) {
                                                                    // Allow decimal input - replace comma with period, but preserve periods
                                                                    normalizedValue =
                                                                        normalizedValue.replace(/,/g, ".");
                                                                    // Allow only numbers, one decimal point, and negative sign
                                                                    normalizedValue =
                                                                        normalizedValue.replace(/[^0-9.-]/g, "");
                                                                    // Ensure only one decimal point
                                                                    const parts = normalizedValue.split(".");
                                                                    if (parts.length > 2) {
                                                                        normalizedValue =
                                                                            parts[0] +
                                                                                "." +
                                                                                parts.slice(1).join("");
                                                                    }
                                                                }
                                                                updateSubItemField(item.id, sectionIndex, subItem.id, "unitCost", normalizedValue);
                                                            }, readOnly: !canEditPrices }));
                                                    })() }));
                                            })(), (() => {
                                                const isShopfrontOver36m = subItem.id === "shopfront-3a" ||
                                                    subItem.title
                                                        .toLowerCase()
                                                        .includes("over 3.6m");
                                                const isAfterHours = subItem.title
                                                    .toLowerCase()
                                                    .includes("after hours");
                                                // For shopfront-3a, display shopfront-4's unitCost in the totalCost field
                                                if (isShopfrontOver36m) {
                                                    const shopfront4Item = section.items.find((item) => item.id === "shopfront-4");
                                                    const shopfront4Value = shopfront4Item?.unitCost || "";
                                                    // Only show input if toggle is YES
                                                    if (subItem.inScope) {
                                                        return (_jsxs("div", { className: `${styles.tableCell} ${styles.tableCellWithLabel}`, children: [_jsx("label", { children: shopfront4Item?.title }), _jsx("input", { type: "text", className: styles.costInput, value: shopfront4Value, onChange: (e) => {
                                                                        // Update shopfront-4's unitCost when user edits
                                                                        const shopfront4Index = section.items.findIndex((item) => item.id === "shopfront-4");
                                                                        if (shopfront4Index !== -1) {
                                                                            const normalizedValue = e.target.value
                                                                                .replace(/,/g, ".")
                                                                                .replace(/[^0-9.-]/g, "");
                                                                            const parts = normalizedValue.split(".");
                                                                            const finalValue = parts.length > 2
                                                                                ? parts[0] +
                                                                                    "." +
                                                                                    parts.slice(1).join("")
                                                                                : normalizedValue;
                                                                            updateSubItemField(item.id, sectionIndex, "shopfront-4", "unitCost", finalValue);
                                                                        }
                                                                    }, readOnly: !canEditPrices })] }));
                                                    }
                                                    else {
                                                        return (_jsx("div", { className: styles.tableCell }));
                                                    }
                                                }
                                                if (isAfterHours) {
                                                    return (_jsx("div", { className: styles.tableCell }));
                                                }
                                                return (_jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.costInput, value: subItem.totalCost || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "totalCost", e.target.value), readOnly: !canEditPrices }) }));
                                            })(), (() => {
                                                const isAfterHours = subItem.title
                                                    .toLowerCase()
                                                    .includes("after hours");
                                                const isShopfrontOver36m = subItem.id === "shopfront-3a" ||
                                                    subItem.title
                                                        .toLowerCase()
                                                        .includes("over 3.6m");
                                                // Hide notes column for shopfront-3a and After Hours items
                                                if (isAfterHours || isShopfrontOver36m) {
                                                    return null;
                                                }
                                                return (_jsx("div", { className: styles.tableCell, children: _jsx("input", { type: "text", className: styles.notesInput, value: subItem.notes || "", onChange: (e) => updateSubItemField(item.id, sectionIndex, subItem.id, "notes", e.target.value) }) }));
                                            })()] }, subItem.id)))] }, sectionIndex))) })) : null }))] }, item.id))) }));
    };
    return (_jsx(_Fragment, { children: _jsxs("div", { className: styles.container, children: [renderPageHeader(), _jsx("div", { className: styles.header, children: _jsx(PageHeaderWithBack, { title: "Create Estimate", onBack: handleBack, breadcrumb: "Estimating/Create Estimate" }) }), error && (_jsx("div", { className: styles.errorBanner, children: _jsx("p", { children: error }) })), _jsx("div", { className: styles.formSection, children: _jsxs("div", { className: styles.formGrid, children: [_jsxs("div", { className: styles.leftColumn, children: [!isNewSite && (_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Site" }), _jsxs("select", { className: "form-select", value: siteId, onChange: (e) => {
                                                    const newId = e.target.value;
                                                    setSiteId(newId);
                                                    if (!newId.trim()) {
                                                        setShoppingCentreId("");
                                                        return;
                                                    }
                                                    sitesService
                                                        .getSite(newId)
                                                        .then(async (resp) => {
                                                        const s = resp.data;
                                                        let companyCid;
                                                        if (s.companyName) {
                                                            try {
                                                                const companiesResponse = await contactsService.getCompaniesWithContactIds();
                                                                const companiesData = companiesResponse.data.data || [];
                                                                const map = new Map();
                                                                companiesData.forEach((row) => {
                                                                    if (row.companyName && row.contactId) {
                                                                        map.set(row.companyName, row.contactId);
                                                                    }
                                                                });
                                                                companyCid = map.get(s.companyName);
                                                            }
                                                            catch {
                                                                /* ignore */
                                                            }
                                                        }
                                                        const id = await resolveShoppingCentrePrefillForSite(s, companyCid);
                                                        setShoppingCentreId(id);
                                                    })
                                                        .catch(() => setShoppingCentreId(""));
                                                }, disabled: loadingSite || !!siteIdParam, children: [_jsx("option", { value: "", children: "Select Site" }), sites.map((site) => (_jsx("option", { value: site.id, children: site.name }, site.id)))] })] })), !isNewSite && (_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Client Name *" }), _jsxs("div", { className: styles.contactDropdownWrapper, ref: companyDropdownRef, "data-dropdown-container": true, children: [_jsxs("div", { className: styles.contactInputWrapper, children: [_jsx("input", { type: "text", className: "form-input", placeholder: "Type here to search", value: companySearchTerm, onChange: (e) => {
                                                                    setCompanySearchTerm(e.target.value);
                                                                    if (!showCompanyDropdown)
                                                                        setShowCompanyDropdown(true);
                                                                }, onFocus: () => setShowCompanyDropdown(true) }), selectedCompanyName && (_jsx("button", { type: "button", className: styles.clearBtn, onClick: handleClearCompany, title: "Clear selection", children: "\u00D7" }))] }), showCompanyDropdown && (_jsx("div", { className: styles.contactDropdownMenu, children: loadingCompanyNames ? (_jsx("div", { className: styles.dropdownLoading, children: "Loading..." })) : companyNames.length > 0 ? (companyNames.slice(0, 50).map((name) => (_jsx("div", { className: styles.contactOption, onClick: () => handleCompanySelect(name), children: name }, name)))) : (_jsx("div", { className: styles.dropdownEmpty, children: companySearchTerm
                                                                ? "No companies found"
                                                                : "Type to search..." })) }))] })] })), !isNewSite && (_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Attention *" }), _jsxs("div", { className: styles.contactDropdownWrapper, ref: attentionDropdownRef, "data-dropdown-container": true, children: [_jsxs("div", { className: styles.contactInputWrapper, children: [_jsx("input", { type: "text", className: "form-input", placeholder: !selectedCompanyName
                                                                    ? "Select a company first"
                                                                    : "Type here to search", value: attentionSearchTerm, onChange: (e) => {
                                                                    setAttentionSearchTerm(e.target.value);
                                                                    if (!showAttentionDropdown)
                                                                        setShowAttentionDropdown(true);
                                                                }, onFocus: () => {
                                                                    if (selectedCompanyName) {
                                                                        setShowAttentionDropdown(true);
                                                                    }
                                                                }, disabled: !selectedCompanyName }), selectedAttentionContactName && (_jsx("button", { type: "button", className: styles.clearBtn, onClick: handleClearAttentionContact, title: "Clear selection", children: "\u00D7" }))] }), showAttentionDropdown && selectedCompanyName && (_jsx("div", { className: styles.contactDropdownMenu, children: filteredAttentionContacts.length > 0 ? (filteredAttentionContacts.map((contact) => (_jsxs("div", { className: styles.contactOption, onClick: () => handleAttentionContactSelect(contact), children: [_jsx("div", { style: { fontWeight: "500" }, children: getContactDisplayName(contact.firstName, contact.lastName, contact.email) }), contact.email && (_jsx("div", { style: { fontSize: "0.85em", color: "#666" }, children: contact.email })), selectedCompanyName && (_jsx("div", { style: { fontSize: "0.85em", color: "#888" }, children: selectedCompanyName }))] }, contact.contactId)))) : (_jsx("div", { className: styles.dropdownEmpty, children: attentionSearchTerm
                                                                ? "No contacts found"
                                                                : "No contacts available" })) }))] })] })), isNewSite && (_jsxs(_Fragment, { children: [_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Site" }), _jsx("input", { type: "text", className: "form-input", value: newSiteName, onChange: (e) => setNewSiteName(e.target.value), required: true })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Tenancy Number" }), _jsx("input", { type: "text", className: "form-input", value: newTenancyNumber, onChange: (e) => setNewTenancyNumber(e.target.value) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Tenancy Name" }), _jsx("input", { type: "text", className: "form-input", value: newTenancyName, onChange: (e) => setNewTenancyName(e.target.value) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Building Type" }), _jsxs("select", { className: "form-select", value: newBuildingType, onChange: (e) => setNewBuildingType(e.target.value), children: [_jsx("option", { value: "", children: "Select Building Type" }), _jsx("option", { value: "COMMERCIAL", children: "Commercial" }), _jsx("option", { value: "RETAIL", children: "Retail" }), _jsx("option", { value: "HOSPITALITY", children: "Hospitality" }), _jsx("option", { value: "INDUSTRIAL", children: "Industrial" })] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Landlord" }), _jsx("input", { type: "text", className: "form-input", value: newLandlord, onChange: (e) => setNewLandlord(e.target.value) })] })] })), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Shopping centre" }), _jsx("div", { className: styles.shoppingCentreField, children: _jsx(SearchableShoppingCentreSelect, { value: shoppingCentreId, onChange: setShoppingCentreId }) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Type" }), _jsxs("div", { className: styles.toggleWrapper, children: [_jsx("span", { className: `${styles.toggleLabel} ${defitType === "full" ? styles.active : ""}`, children: "Full Defit" }), _jsx("button", { className: styles.defitToggleButton, onClick: () => setDefitType(defitType === "full" ? "partial" : "full"), "aria-label": "Toggle defit type", children: _jsx("span", { className: `${styles.defitToggleSlider} ${defitType === "partial" ? styles.right : ""}` }) }), _jsx("span", { className: `${styles.toggleLabel} ${defitType === "partial" ? styles.active : ""}`, children: "Partial Defit" })] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Estimation Name" }), _jsx("input", { type: "text", className: "form-input", value: quoteName, onChange: (e) => setQuoteName(e.target.value) })] }), _jsxs("div", { className: `${styles.formGroupCol} form-group-col`, children: [_jsx("label", { className: "form-label", children: "Estimation Description" }), _jsx("textarea", { className: "form-textarea", value: quoteDescription, onChange: (e) => setQuoteDescription(e.target.value), rows: 5 })] })] }), _jsxs("div", { className: styles.rightColumn, children: [isNewSite && (_jsxs(_Fragment, { children: [_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Address" }), _jsx("input", { type: "text", className: "form-input", value: newAddress, onChange: (e) => setNewAddress(e.target.value) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Suburb" }), _jsxs("div", { ref: suburbDropdownRef, className: styles.contactDropdownWrapper, "data-dropdown-container": true, children: [_jsxs("div", { className: styles.contactInputWrapper, children: [_jsx("input", { type: "text", className: "form-input", value: suburbSearchTerm, onChange: (e) => {
                                                                            const newValue = e.target.value;
                                                                            setSuburbSearchTerm(newValue);
                                                                            setShowSuburbDropdown(true);
                                                                            setNewSuburb(newValue);
                                                                        }, onFocus: () => setShowSuburbDropdown(true), placeholder: "Type here to search" }), suburbSearchTerm && (_jsx("button", { type: "button", className: styles.clearBtn, onClick: handleClearSuburb, children: "\u00D7" }))] }), showSuburbDropdown && (_jsx("div", { className: styles.contactDropdownMenu, children: filteredSuburbs.length === 0 && suburbSearchTerm ? (_jsx("div", { className: styles.dropdownEmpty, children: "No suburbs found" })) : filteredSuburbs.length === 0 ? (_jsx("div", { className: styles.dropdownEmpty, children: "Start typing to search suburbs..." })) : (filteredSuburbs.map((suburb, index) => (_jsx("button", { type: "button", className: `${styles.contactOption} ${newSuburb === suburb.name
                                                                        ? styles.selected
                                                                        : ""}`, onClick: () => handleSuburbSelect(suburb), children: _jsxs("span", { className: styles.contactName, children: [suburb.name, ", ", suburb.state, " ", suburb.postcode] }) }, `${suburb.name}-${suburb.state}-${index}`)))) }))] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "State" }), _jsx("input", { type: "text", className: "form-input", value: newState, onChange: (e) => setNewState(e.target.value), readOnly: true })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Postcode" }), _jsx("input", { type: "text", className: "form-input", value: newPostcode, onChange: (e) => setNewPostcode(e.target.value), readOnly: true })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Client Name *" }), _jsxs("div", { className: styles.contactDropdownWrapper, ref: companyDropdownRef, "data-dropdown-container": true, children: [_jsxs("div", { className: styles.contactInputWrapper, children: [_jsx("input", { type: "text", className: "form-input", placeholder: "Type here to search", value: companySearchTerm, onChange: (e) => {
                                                                            setCompanySearchTerm(e.target.value);
                                                                            if (!showCompanyDropdown)
                                                                                setShowCompanyDropdown(true);
                                                                        }, onFocus: () => setShowCompanyDropdown(true) }), selectedCompanyName && (_jsx("button", { type: "button", className: styles.clearBtn, onClick: handleClearCompany, title: "Clear selection", children: "\u00D7" }))] }), showCompanyDropdown && (_jsx("div", { className: styles.contactDropdownMenu, children: loadingCompanyNames ? (_jsx("div", { className: styles.dropdownLoading, children: "Loading..." })) : companyNames.length > 0 ? (companyNames.slice(0, 50).map((name) => (_jsx("div", { className: styles.contactOption, onClick: () => handleCompanySelect(name), children: name }, name)))) : (_jsx("div", { className: styles.dropdownEmpty, children: companySearchTerm
                                                                        ? "No companies found"
                                                                        : "Type to search..." })) }))] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Attention *" }), _jsxs("div", { className: styles.contactDropdownWrapper, ref: attentionDropdownRef, "data-dropdown-container": true, children: [_jsxs("div", { className: styles.contactInputWrapper, children: [_jsx("input", { type: "text", className: "form-input", placeholder: !selectedCompanyName
                                                                            ? "Select a company first"
                                                                            : "Type here to search", value: attentionSearchTerm, onChange: (e) => {
                                                                            setAttentionSearchTerm(e.target.value);
                                                                            if (!showAttentionDropdown)
                                                                                setShowAttentionDropdown(true);
                                                                        }, onFocus: () => {
                                                                            if (selectedCompanyName) {
                                                                                setShowAttentionDropdown(true);
                                                                            }
                                                                        }, disabled: !selectedCompanyName }), selectedAttentionContactName && (_jsx("button", { type: "button", className: styles.clearBtn, onClick: handleClearAttentionContact, title: "Clear selection", children: "\u00D7" }))] }), showAttentionDropdown && selectedCompanyName && (_jsx("div", { className: styles.contactDropdownMenu, children: filteredAttentionContacts.length > 0 ? (filteredAttentionContacts.map((contact) => (_jsxs("div", { className: styles.contactOption, onClick: () => handleAttentionContactSelect(contact), children: [_jsx("div", { style: { fontWeight: "500" }, children: getContactDisplayName(contact.firstName, contact.lastName, contact.email) }), contact.email && (_jsx("div", { style: {
                                                                                fontSize: "0.85em",
                                                                                color: "#666",
                                                                            }, children: contact.email })), selectedCompanyName && (_jsx("div", { style: {
                                                                                fontSize: "0.85em",
                                                                                color: "#888",
                                                                            }, children: selectedCompanyName }))] }, contact.contactId)))) : (_jsx("div", { className: styles.dropdownEmpty, children: attentionSearchTerm
                                                                        ? "No contacts found"
                                                                        : "No contacts available" })) }))] })] })] })), _jsxs("div", { className: styles.dateTimeSection, children: [_jsxs("div", { className: styles.dateTimeRow, children: [_jsx("div", { className: styles.dateTimeBlock, children: _jsxs("div", { className: styles.dateTimeInputs, children: [_jsx("label", { className: `form-label ${styles.dateTimeLabel}`, children: "Commencement Date and Time" }), _jsx("div", { className: styles.dateInput, children: _jsx(CommencementDateInput, { className: "form-input", value: commencementDate, onChange: setCommencementDate }) }), _jsx("div", { className: styles.timeInput, children: _jsx("input", { type: "time", className: "form-input", value: commencementTime, onChange: (e) => setCommencementTime(e.target.value) }) })] }) }), _jsx("div", { className: styles.dateTimeBlock, children: _jsxs("div", { className: styles.dateTimeInputs, children: [_jsx("label", { className: `form-label ${styles.dateTimeLabel}`, children: "Completion Date and Time" }), _jsx("div", { className: styles.dateInput, children: _jsx(DateInput, { className: "form-input", value: completionDate, onChange: setCompletionDate, onInvalid: () => showError("Please enter a valid date.") }) }), _jsx("div", { className: styles.timeInput, children: _jsx("input", { type: "time", className: "form-input", value: completionTime, onChange: (e) => setCompletionTime(e.target.value) }) })] }) })] }), _jsxs("div", { className: styles.durationGroup, children: [_jsx("label", { className: "form-label", children: "Project Duration (days)" }), _jsx("input", { type: "number", className: styles.durationInput, value: projectDuration, onChange: (e) => setProjectDuration(e.target.value) })] })] }), _jsxs("div", { children: [_jsx("h3", { className: styles.sectionTitle, children: "AREA INFORMATION" }), _jsx("div", { className: styles.areaInfoGrid, children: areaInfo.map((item, index) => (_jsxs("div", { className: styles.areaInfoRow, children: [_jsx("label", { className: styles.areaLabel, children: item.label }), _jsx("input", { type: "number", className: styles.areaInput, value: item.value, onFocus: (e) => {
                                                                if (item.value === 0)
                                                                    e.target.select();
                                                            }, onChange: (e) => handleAreaInfoChange(index, Number(e.target.value)) })] }, index))) })] })] })] }) }), _jsxs("div", { className: styles.workDetailsSection, children: [_jsx("h3", { className: styles.workDetailsTitle, children: "Work Details" }), _jsx("div", { className: styles.categoryTabsWrapper, children: _jsx("div", { className: styles.categoryTabList, children: WORK_DETAILS_CATEGORY_TABS.map((tab) => (_jsx("button", { className: `${styles.categoryTab} ${activeWorkCategory === tab.id ? styles.active : ""}`, onClick: () => {
                                        setActiveWorkCategory(tab.id);
                                        setExpandedAccordions(new Set());
                                    }, children: tab.label }, tab.id))) }) }), activeWorkCategory !== "summary" && (_jsxs("div", { className: styles.accordionControls, children: [_jsxs("button", { className: styles.controlBtn, onClick: handleExpandAll, children: [_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M7 13l5 5 5-5M7 6l5 5 5-5" }) }), "Expand All"] }), _jsxs("button", { className: styles.controlBtn, onClick: handleCollapseAll, children: [_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: _jsx("path", { d: "M17 11l-5-5-5 5M17 18l-5-5-5 5" }) }), "Collapse All"] })] })), _jsx("div", { className: styles.accordionContainer, children: renderAccordionContent() })] }), _jsx("div", { className: styles.globalActionFooter, children: _jsx("button", { className: styles.saveEstimateButton, onClick: handleSave, disabled: saving, children: saving ? "Creating..." : "Create Estimate" }) })] }) }));
};
